import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { useAuthStore, useDeviceTrustStore, useTaskStore } from '../store';
import { getDeviceToken } from './deviceToken';
import {
  PRACTICE_LOST_LOOP_WINDOW_MS, PRACTICE_REFUSAL_HOLD_MS, getPracticeReloadPending, readPracticeLostRecord,
  removePracticeLostRecord, setPracticeReloadPending, writePracticeLostRecord,
} from './practiceLostRecord';
import type { AcceptPayload } from '../components/oversight/integrity';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// ─── API CLIENT ───────────────────────────────────────────────────────────────

const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
  paramsSerializer: (params) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, String(v)));
      } else if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    return searchParams.toString();
  },
});

// Request interceptor — attach auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    const practice = useAuthStore.getState().currentPractice;

    // ai.346 DR1 (L5-4) — a refused practice was cleared and its reload has not happened (cancelled at "Reload
    // site?", or still pending). The server runs a practice user's header-less request at HOME, so until the reload
    // or a practice pick nothing but /auth/* leaves: it is refused HERE, before it is sent, caller-pinned requests
    // included. A plain body-shaped error with no response and no status passes the response interceptor
    // untouched; the non-enumerable preflight_refusal marks it as never sent (RecordsReleaseDetailPanel
    // sendResultUnknown reads it), so no door calls its result unknown.
    if (getPracticeReloadPending() !== null && !practice && !config.url?.startsWith('/auth/')) {
      const refusal = { message: 'Reload the page to continue.', code: 'PRACTICE_RELOAD_REQUIRED' };
      Object.defineProperty(refusal, 'preflight_refusal', { value: true, enumerable: false });
      return Promise.reject(refusal);
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach the selected practice on every request EXCEPT the /auth/me identity
    // probe. /auth/me runs through the same authenticate() middleware that 403s a
    // stale/foreign X-Practice-ID (PRACTICE_SWITCH_DENIED), yet its response never
    // depends on the selected practice. Sending a stale header here would 403 the
    // very first identity fetch on boot — before useCurrentUser() can reconcile or
    // clear the practice — leaving the app unable to load while the pollers hammer
    // a permanent 403. Omitting it keeps identity resolution independent of the
    // (possibly stale) practice selection.
    // A per-request X-Practice-ID (set by callers that target a specific
    // practice, e.g. the platform bulk-eligibility screen) wins over the
    // global selection — don't overwrite it.
    if (practice && !config.url?.endsWith('/auth/me') && !config.headers['X-Practice-ID']) {
      config.headers['X-Practice-ID'] = practice.practice_id;
    }
    // App-level trusted-device token (attests this device was verified). Sent
    // only if present; the server ignores it unless REQUIRE_TRUSTED_DEVICE is on.
    const deviceToken = getDeviceToken();
    if (deviceToken) {
      config.headers['X-Device-Token'] = deviceToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle errors globally.
// 401 handling is deliberately conservative: it's easy to fall into a
// login ↔ dashboard loop if we force a logout + redirect on every 401.
// We only force logout when all three are true:
//   - we HAD a token (so this is a real token rejection, not a race during
//     Auth0 rehydration where a query fires before the token is in state),
//   - we are NOT already on /login (no self-refresh loops),
//   - we have not already triggered a logout in the last 2 seconds (so a
//     burst of parallel refetches doesn't kick us out N times).
// A 401 during boot (no token yet) just fails the individual request —
// React Query will retry on its own once auth is ready.
let lastLogoutAt = 0;
// Debounce for the PRACTICE_SWITCH_DENIED loop-breaker below (see the 403 branch).
let lastPracticeResetAt = 0;
// ai.358 - browsers have Blob.prototype.text; jsdom 16.7.0 (the CRA 5 test env) does not. Both arms stay.
function readBlobText(blob: Blob): Promise<string> {
  if (typeof (blob as any).text === 'function') return (blob as any).text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsText(blob);
  });
}
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // ai.358 (S10 A) - a responseType:'blob' request receives the server's { message, code } refusal as a Blob, so
    // neither the 403 branches below nor the error contract can read it. Decode a JSON-typed Blob in place, for EVERY
    // error status, before anything reads error.response.data. A non-JSON Blob (a PDF, an HTML 502), a body that does
    // not parse, or one that parses to anything but a plain object is left exactly as it came. Never throws. A JSON
    // body takes NO await here, so its branches below still run in the same macrotask (DR1).
    const raw = error.response?.data;
    if (typeof Blob !== 'undefined' && raw instanceof Blob && /application\/json/i.test(raw.type || '')) {
      try {
        const parsed = JSON.parse(await readBlobText(raw));
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) error.response.data = parsed;
      } catch { /* leave the Blob */ }
    }
    if (error.response?.status === 401) {
      const hasToken = !!useAuthStore.getState().token;
      const onLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login';
      const recentlyLoggedOut = Date.now() - lastLogoutAt < 2000;
      // Skip auto-logout for /auth/me — useCurrentUser's queryFn owns the
      // error path for the initial user fetch. Letting the interceptor
      // redirect here races with Auth0's cached session and can loop
      // /login ↔ /dashboard if Auth0 still reports isAuthenticated=true.
      const url = (error.config?.url || '') as string;
      const isAuthMe = url.endsWith('/auth/me');

      if (hasToken && !onLoginPage && !recentlyLoggedOut && !isAuthMe) {
        lastLogoutAt = Date.now();
        // Persistent loop-breaker: the redirect below is a FULL-PAGE navigation,
        // which resets `lastLogoutAt`. Without this flag, a stale-but-present Auth0
        // session would ping-pong /login ↔ /dashboard on every expired-token 401
        // (and hammer the auth rate limiter). This flag survives the reload —
        // LoginPage reads it and shows the sign-in screen instead of auto-bouncing.
        try { sessionStorage.setItem('previsit_forced_logout', String(Date.now())); } catch { /* ignore */ }
        // ai.346 DR1 (L5-1) — the lost-practice notice belongs to the user being logged out (never throws).
        removePracticeLostRecord();
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }
    // ai.346 DR1 (L6-2) — set in the PRACTICE_SWITCH_DENIED branch below, read at the body rejection.
    let holdRejection = false;
    if (error.response?.status === 403) {
      const code = error.response?.data?.code;
      // ai.359 (D-2026-10-09-01 S1 A, S2 B) - a clinic whose lifecycle EXITED refuses PRACTICE_NOT_ACTIVE; recover exactly
      // as for a refused switch (DR1), except on /auth/me, which App.tsx turns into the suspended-practice screen (a reload
      // there would loop - critic F1). Not a second branch, not an extraction: DR1's sequence is reused byte for byte.
      const refusedUrl = (error.config?.url || '') as string;
      const isPracticeRefusal = code === 'PRACTICE_SWITCH_DENIED'
        || (code === 'PRACTICE_NOT_ACTIVE' && !refusedUrl.endsWith('/auth/me'));
      // App-level device trust: an unverified device hit a PHI route. Trigger
      // the verification modal instead of a generic "access denied".
      if (code === 'DEVICE_NOT_TRUSTED') {
        useDeviceTrustStore.getState().setVerificationRequired(true);
      } else if (isPracticeRefusal) {
        // Safety net — the primary fix clears the practice in useCurrentUser, but
        // if a stale X-Practice-ID for an unassigned practice ever slips through,
        // clear it here so every subsequent request omits the header. That both
        // stops the permanent 403 (which otherwise trips the rate limiter → 429
        // flood) and surfaces the practice picker / pending-assignment banner
        // (currentPractice === null). Debounced like the 401 path so a burst of
        // parallel pollers can't thrash the store.
        // ai.346 (D-2026-10-07-01, D-2026-10-08-07 S1 A): practice users are now refused too. The
        // comparison runs FIRST: a PINNED request (a caller-set X-Practice-ID for another practice)
        // that is refused leaves the user's current, valid practice untouched - the door's own error
        // path reports it (N03 rule; FR8). Otherwise clear, remember the lost practice's name for the
        // one-line notice (PracticeLostBanner), and reload so no unpinned screen keeps working at the
        // server's home pin under no name.
        // ai.346 DR1 (DESIGN_REVIEW_2026-10-08 L5-1, L5-4, L7-1, L2-3, L6-2): the record is OWNED
        // (lib/practiceLostRecord), the same refusal soon after a refusal reload is held without another reload,
        // the page flag stops every request until the reload, and the reload lands on the list.
        const current = useAuthStore.getState().currentPractice;
        const sent = error.config?.headers?.get?.('X-Practice-ID') ?? error.config?.headers?.['X-Practice-ID'];
        let pinnedElsewhere = false;
        if (current && sent && String(sent) !== current.practice_id) {
          // B2 - a pinned request for another practice was refused; nothing to clear.
          pinnedElsewhere = true;
        } else if (current && Date.now() - lastPracticeResetAt > 2000) {
          lastPracticeResetAt = Date.now();
          console.warn(`[practice] Cleared stale practice selection after ${code}`);
          const now = Date.now();
          const userId = useAuthStore.getState().user?.user_id;
          const prior = readPracticeLostRecord();
          if (prior !== null && prior !== 'invalid' && prior.user_id === userId
            && prior.practice_id === current.practice_id
            && now - prior.at >= 0 && now - prior.at < PRACTICE_LOST_LOOP_WINDOW_MS) {
            // L7-1 BREAKER - this user lost this same practice moments ago and the reload selected it again (an
            // /auth/me vs middleware disagreement): another reload would loop. Clear and stop - no record, no flag,
            // no landing, no reload. The notice explains it (V2, no Reload button).
            useAuthStore.getState().setCurrentPractice(null);
          } else {
            // L5-1 - the OWNED record for the notice after the reload, and for the breaker. No user, no record.
            if (userId) writePracticeLostRecord({ user_id: userId, practice_id: current.practice_id, name: current.name, at: now });
            // L5-4 - the page flag, BEFORE the clear: until the reload, nothing but /auth/* leaves without a practice
            // (the request interceptor), even when the user cancels "Reload site?".
            setPracticeReloadPending(current.name);
            useAuthStore.getState().setCurrentPractice(null);
            // L2-3 - land on the list, not on the lost practice's task or fax: close the persisted panel and drop
            // the object query (?task= / ?focus=) so the notice is in view after the reload. Never blocks the reload.
            try {
              useTaskStore.getState().closePanel();
              window.history.replaceState(window.history.state, '', window.location.pathname);
            } catch { /* ignore */ }
            if (typeof window !== 'undefined') window.location.reload();
          }
        }
        holdRejection = !pinnedElsewhere && getPracticeReloadPending() !== null;
      } else {
        console.error('Access denied');
      }
    }
    // ─── THE ERROR CONTRACT (load-bearing — see DESIGN_REVIEW_2026-07-13_nrr-letter-flow.md) ───
    // We reject with the response BODY ({ message, code }) because many call sites read `err.code`.
    // But just as many read the AXIOS shape (`err.response.data.code`, `err.response.status`) — and
    // on a body-shaped rejection those silently resolve to `undefined`. Nothing throws; the branch
    // simply never runs. That is what killed EVERY coded recovery path in the fax/records UI:
    //   • SEND_UNCONFIRMED never disarmed Send after an indeterminate dispatch — so a second click
    //     RE-FAXED a patient's records to someone who may already have them;
    //   • "Prepare again" never rendered, so a reclaimed records letter was an inescapable dead end
    //     (with the server's own message telling the operator to press a button that wasn't there);
    //   • PAGES_NOT_VIEWED and the terminal gate refusals looped forever.
    // Verified dead in the SHIPPED bundle (main.c09c6a17.js): 10 of 24 recovery tests read `.response`.
    //
    // FIX: still reject with the body — but re-attach `response` + `status` NON-ENUMERABLY, so BOTH
    // shapes resolve on the same value and neither convention is wrong:
    //     err.code === err.response.data.code        err.status === err.response.status
    // Non-enumerable ⇒ JSON.stringify / logging / spread are byte-identical to before.
    // In NEW code prefer errCode(err) / errStatus(err) / errMessage(err) from lib/apiError.ts.
    //
    // 🔐 SECURITY: attach a MINIMAL SURROGATE — never the real AxiosResponse. The genuine object
    // carries `config`, and `config.headers` carries the `Authorization: Bearer <JWT>` (and the
    // X-Practice-ID / X-Device-Token). Hanging that off every rejected error would put the caller's
    // ACCESS TOKEN one property hop from any `console.error(err)` (Chrome renders non-enumerables),
    // any error reporter, and anything walking getOwnPropertyNames. Call sites only ever read
    // `.response.data` and `.response.status`, so that is ALL the surrogate exposes: no token, no
    // headers, no request config.
    // ⚠️ SCOPE OF THAT CLAIM — it holds for THIS branch only (a JSON object body). The fall-through
    // below still rejects the RAW AxiosError on a network failure / non-object body, and that error
    // DOES carry config.headers. Pre-existing, unchanged by this batch, and tracked — do not read
    // this comment as "no error in the app can ever carry a token".
    const body = error.response?.data;
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      try {
        // Guard: never clobber a real field the server sent (an error body could legitimately carry
        // its own `status`). If it did, the axios-shaped read was already unreliable for it anyway.
        if (!('response' in body)) {
          Object.defineProperty(body, 'response', {
            // `data: body` closes the axios chain back on itself, so `err.response.data.code` === `err.code`.
            value: { data: body, status: error.response?.status, statusText: error.response?.statusText },
            enumerable: false, writable: true, configurable: true,
          });
        }
        if (!('status' in body)) {
          Object.defineProperty(body, 'status', {
            value: error.response?.status, enumerable: false, writable: true, configurable: true,
          });
        }
      } catch {
        // A frozen/sealed/Proxy body (not reachable via axios' JSON parse today, but this is the one
        // function EVERY error flows through). DEGRADE, NEVER THROW: a throw here would replace the
        // server's 409 body with a TypeError, so every coded recovery path would read `undefined`
        // again — resurrecting the exact bug this batch fixes, on every error in the app.
        // The body shape still resolves; only the axios-shaped reads stay dark on such a body.
      }
      // ai.346 DR1 (L6-2) — a refusal of the practice this page works in, while the page waits for its reload: the
      // caller's alert or toast would fire in the page being replaced. Hold the SAME body (response/status intact)
      // a few seconds, then reject; B2 and every other code reject at once. Always settles.
      if (holdRejection) return new Promise((_, reject) => setTimeout(() => reject(body), PRACTICE_REFUSAL_HOLD_MS));
      return Promise.reject(body);
    }
    // Fall-through: a network failure (no response at all) or a non-object body (a CloudFront/App
    // Runner HTML 502). Byte-identical to pre-batch behaviour. NOTE this still hands back the raw
    // AxiosError, which DOES carry config.headers (the Bearer token) — pre-existing, and the reason
    // no consumer may `console.error(err)` a raw error. Use errMessage(err) (lib/apiError.ts).
    return Promise.reject(body || error);
  }
);

export default apiClient;

// ─── API FUNCTIONS ────────────────────────────────────────────────────────────

// Auth
export const authApi = {
  getMe: () => apiClient.get('/auth/me'),
  refreshToken: () => apiClient.post('/auth/refresh'),
  logout: () => apiClient.post('/auth/logout'),
};

// Practices
export const practicesApi = {
  getAll: () => apiClient.get('/practices'),
  getById: (id: string) => apiClient.get(`/practices/${id}`),
  update: (id: string, data: unknown) => apiClient.put(`/practices/${id}`, data),
  // C3(b) — platform-admin per-practice data-counts (Clients → Data tab).
  getDataCounts: (id: string) => apiClient.get(`/practices/${id}/data-counts`),
  // Hotfix 2026-09-30 — { live: true } sends ?statuses=live: every practice but suspended / terminated. With no
  // argument the list stays status = 'active' (the eligibility-upload picker relies on that: its files are PHI).
  getAssigned: (opts?: { live?: boolean }) => (opts?.live
    ? apiClient.get('/practices/assigned', { params: { statuses: 'live' } })
    : apiClient.get('/practices/assigned')),
  // Phase A2 follow-up #8 — platform_admin creates a client org + invites the
  // first practice_admin + attaches platform users in any per-practice role.
  create: (data: {
    practice_name: string;
    specialty?: string;
    status: 'sandbox' | 'activation';
    organization_id?: string | null;   // T6b — assign the new practice to a parent org
    admin: { first_name: string; last_name: string; email: string; login_method: string };
    assignments?: Array<{ platform_user_id: string; role: 'account_manager' | 'operator' | 'supervisor' }>;
  }) => apiClient.post('/practices', data),
  // T6b — re-parent a practice into an org (platform_admin ONLY — a dedicated route, NOT the generic PUT).
  setOrganization: (id: string, organization_id: string | null) =>
    apiClient.put(`/practices/${id}/organization`, { organization_id }),
  // Phase A2 #9 — platform_admin sets a practice's SSO auto-join domains + flag.
  updateDomains: (id: string, data: { sso_email_domains?: string[]; domain_autojoin_enabled?: boolean }) =>
    apiClient.patch(`/practices/${id}/domains`, data),
  updateSecurityPolicy: (id: string, data: unknown) => apiClient.put(`/practices/${id}/security-policy`, data),
  updateNotificationDefaults: (id: string, data: unknown) => apiClient.put(`/practices/${id}/notification-defaults`, data),
  getDocumentPolicies: (id: string) => apiClient.get(`/practices/${id}/document-policies`),
  updateDocumentPolicies: (id: string, data: unknown) => apiClient.put(`/practices/${id}/document-policies`, data),
  getEmrConfig: (id: string) => apiClient.get(`/practices/${id}/emr-config`),
  updateEmrConfig: (id: string, data: unknown) => apiClient.put(`/practices/${id}/emr-config`, data),
  getEmrConfigDefaults: () => apiClient.get('/practices/emr-config-defaults'),
  updateOwnershipMode: (id: string, mode: 'import_only' | 'full_control') =>
    apiClient.put(`/practices/${id}/ownership-mode`, { mode }),
  updateRecordsApprovers: (id: string, data: { records_approver_user_ids: string[] }) =>
    apiClient.put(`/practices/${id}/records-approvers`, data),
  // W4a inversion — the RESOLVED records-release approver ids through the flag-switched backend
  // source (approverSource). Frontend surfaces read THIS, never the raw config key, so they
  // auto-follow the grant-authority flip.
  getRecordsApprovers: (id: string) => apiClient.get(`/practices/${id}/records-approvers`),
  // F2 — per-practice records-release policy toggles (practices.config JSONB): the clinical-review-
  // before-release overlay (records_access_lane_review) + the charge-patients default. GET hydrates
  // the Settings tab; PATCH writes ONLY the present keys. account_manager / practice_admin /
  // platform_admin (server-gated via canConfigurePractice).
  getRecordsReleaseConfig: (id: string) => apiClient.get(`/practices/${id}/records-release-config`),
  setRecordsReleaseConfig: (
    id: string,
    // ROI Phase 3 — charge_amount_cents = the default patient fee (cents, 0..1000000), written
    // alongside the two toggles. Server whitelists it on the same config route.
    data: { records_access_lane_review?: boolean; charge_patients_for_records_default?: boolean; charge_amount_cents?: number },
  ) => apiClient.patch(`/practices/${id}/records-release-config`, data),
  // ROI Phase 3 — recent distinct requesters (≤20) for the intake autofill datalist.
  getRecordsReleaseRequesters: (id: string) =>
    apiClient.get(`/practices/${id}/records-release-requesters`),
  // G7 — per-practice safe fax-attach toggle (platform_admin / account_manager).
  setFaxAttachEnabled: (id: string, fax_attach_enabled: boolean) =>
    apiClient.put(`/practices/${id}/fax-attach-enabled`, { fax_attach_enabled }),
  // MRR P7 — set the governing state for records-release deadlines/fees (null = federal floor).
  setRecordsReleaseState: (id: string, state_code: string | null) =>
    apiClient.patch(`/practices/${id}/records-release-state`, { state_code }),
  // ROI-M 3c — set the practice IANA timezone (mig 206; null = UTC default). Governs the practice-local
  // "today" used to default a records-release request's received date (§164.524 anchor). Server validates
  // the IANA name + canConfigurePractice-gates. Surfaced for hydration in getRecordsReleaseConfig.
  setPracticeTimezone: (id: string, timezone: string | null) =>
    apiClient.patch(`/practices/${id}/practice-timezone`, { timezone }),
  // Fax-task close-out: per-practice "file faxes to patient Documents" segregation
  // switch (mig 106). platform_admin / platform_account_manager only (server-gated).
  setFaxFilingEnabled: (id: string, files_faxes_to_documents: boolean) =>
    apiClient.patch(`/practices/${id}/fax-settings`, { files_faxes_to_documents }),
  // Per-practice fax settings (mig 106 + 110): file-faxes-to-Documents AND the stuck-fax
  // alert toggles share the ONE PATCH /:id/fax-settings route — each field optional, only
  // the present ones are written. platform_admin / platform_account_manager only.
  setFaxSettings: (
    id: string,
    data: {
      files_faxes_to_documents?: boolean;
      stuck_fax_alerts_enabled?: boolean;
      stuck_fax_alert_cc_practice_admin?: boolean;
    },
  ) => apiClient.patch(`/practices/${id}/fax-settings`, data),
  // Send M3 — per-practice "restrict fax sending to designated senders" toggle
  // (central_fax_restrict_senders) + the resolved designated-sender list. GET hydrates the
  // admin surface; PATCH writes the toggle; getCentralFaxSenders returns the active users
  // holding central_fax.send (mirrors getRecordsApprovers). Server-gated to configurers.
  getCentralFaxConfig: (id: string) => apiClient.get(`/practices/${id}/central-fax-config`),
  // Cover Pages P1 — the same PATCH now also carries the two cover toggles: R1
  // (central_fax_provider_cover, absent=false) + R2 (central_fax_rcmtask_cover, absent=true).
  // Each key optional; only the present ones are written (server ||-merges + audits per key).
  // GET (getCentralFaxConfig) now returns all the keys.
  // View-before-send Phase B — central_fax_records_cover_template: the RECORDS-RELEASE default
  // cover (the unattended records rail deliberately does NOT follow the composer default —
  // unattended output must be predictable). Value = an approved FIELDLESS template's uuid
  // (records sends have no operator to fill fields — §8.1) or 'house' (the built-in cover;
  // absent ⇒ House too). Server-validated: a non-'house' id must be an approved fieldless
  // template of THIS practice; a template gone stale/retired/field-bearing later resolves to
  // House with a PHI-free audit.
  updateCentralFaxConfig: (
    id: string,
    data: {
      central_fax_restrict_senders?: boolean;
      central_fax_provider_cover?: boolean;
      central_fax_rcmtask_cover?: boolean;
      central_fax_records_cover_template?: string; // uuid | 'house'
    },
  ) => apiClient.patch(`/practices/${id}/central-fax-config`, data),
  getCentralFaxSenders: (id: string) => apiClient.get(`/practices/${id}/central-fax-senders`),
  // Send Fax follow-up — the grouped candidate roster the "Who can send faxes" + "User send
  // lines" pickers draw from: ACTIVE practice users AND platform users ASSIGNED to this practice.
  // BE contract: { data: { practice_users: [{user_id,name,email}], platform_users: [{user_id,name,email}] } }
  // (read each group defensively — either may be absent). canConfigurePractice-gated.
  getCentralFaxSenderCandidates: (id: string) => apiClient.get(`/practices/${id}/central-fax-sender-candidates`),
  // Per-user assigned send line (multi-line practices). GET returns the whole map; the BE nests it
  // under `central_fax_user_lines` → { data: { central_fax_user_lines: { <user_id>: <connection_id> } } }
  // (read both the nested and flat shapes defensively). PUT sets one user's line, or clears it with
  // { connection_id: null }. canConfigurePractice-gated (PHI-free staff/line-id config).
  getCentralFaxUserLines: (id: string) => apiClient.get(`/practices/${id}/central-fax-user-lines`),
  setCentralFaxUserLine: (id: string, data: { user_id: string; connection_id: string | null }) =>
    apiClient.put(`/practices/${id}/central-fax-user-lines`, data),
};

// Patients
export const patientsApi = {
  getAll: (params?: unknown) => apiClient.get('/patients', { params }),
  getById: (id: string) => apiClient.get(`/patients/${id}`),
  create: (data: unknown) => apiClient.post('/patients', data),
  update: (id: string, data: unknown) => apiClient.put(`/patients/${id}`, data),
  getInsurances: (patientId: string) => apiClient.get(`/patients/${patientId}/insurances`),
  // MRR P2 — Accounting of Disclosures (§164.528) register + the 60-day request tracker.
  // Backend gates these to practice_admin / platform_admin / account_manager.
  getAccountingOfDisclosures: (patientId: string) =>
    apiClient.get(`/patients/${patientId}/accounting-of-disclosures`),
  getAccountingRequests: (patientId: string) =>
    apiClient.get(`/patients/${patientId}/accounting-requests`),
  logAccountingRequest: (patientId: string, data: { request_date?: string; notes?: string }) =>
    apiClient.post(`/patients/${patientId}/accounting-requests`, data),
  markAccountingRequestFulfilled: (patientId: string, requestId: string, data: { fulfilled_at?: string; note?: string }) =>
    apiClient.patch(`/patients/${patientId}/accounting-requests/${requestId}`, data),
  // Platform bulk-eligibility screen: search patients at an EXPLICIT practice
  // (which may differ from the globally selected one) via per-request header.
  searchAtPractice: (practiceId: string, search: string, limit = 10) =>
    apiClient.get('/patients', {
      params: { search, limit, offset: 0 },
      headers: { 'X-Practice-ID': practiceId },
    }),
};

// Appointments
export const appointmentsApi = {
  getAll: (params?: unknown) => apiClient.get('/appointments', { params }),
  getById: (id: string) => apiClient.get(`/appointments/${id}`),
  create: (data: unknown) => apiClient.post('/appointments', data),
  update: (id: string, data: unknown) => apiClient.put(`/appointments/${id}`, data),
  getChildren: (id: string) => apiClient.get(`/appointments/${id}/children`),
  getTasks: (id: string) => apiClient.get(`/appointments/${id}/tasks`),
  getProviders: () => apiClient.get('/appointments/providers'),
  getTypes: () => apiClient.get('/appointments/types'),
};

// P8-C — CLEAR the patient off a case linked to the WRONG person ("this isn't our patient"). The
// preview PREVIEWS its own refusal (`blocked`) instead of throwing, so the operator reads why the
// clear is unavailable inside the same modal.
export interface ClearPreview {
  clear: true;
  prior_patient_id: string | null;
  prior_patient_name: string | null;
  graph_hash: string;
  /** ALL step children — open AND already completed. */
  steps_to_cancel: number;
  /** records_release_source/_draft documents on the subtree. */
  documents_to_void: number;
  fax_will_unlink: boolean;
  /** Any case document filed onto the WRONG chart — exactly `chart_docs_to_unfile > 0`. */
  chart_doc_will_unfile: boolean;
  /**
   * HOW MANY documents leave the prior chart. The request PDF is rarely the only one: the apply
   * un-files EVERY live case-subtree document sitting on that chart (the fax PDF, a MANUAL-origin
   * request doc, letters, authorizations). Render this count — never the singular.
   */
  chart_docs_to_unfile: number;
  /**
   * FOUR record types captured against the wrong patient: Step-1 requester authority + §164.508
   * authorizations + 42 CFR Part 2 consents (ACTIVE rows only) + a VALIDATED §164.512 legal basis.
   * The apply RETIRES them by TWO different acts — the first three are marked superseded_at (NEVER
   * revoked_at: under 42 CFR §2.31 a REVOCATION is the PATIENT's own act, so an administrative
   * correction that stamped it would state something false about the patient; the part2 read
   * predicate's `revoked_at IS NULL` is a filter, not a write). The §164.512 basis is instead
   * UN-VALIDATED (validated_at cleared), because a stale VALIDATED row reads as SATISFIED and would
   * flip that gate the wrong way. Every row is KEPT; the field name is the frozen server contract,
   * the act is "retired", not "deleted".
   */
  verification_rows_to_remove: number;
  register_will_clear: boolean;
  blocked: null | { code: string; message: string };
}
export interface ClearResult {
  cleared: true;
  steps_cancelled: number;
  documents_voided: number;
  fax_unlinked: boolean;
  chart_doc_unfiled: boolean;
  /** The parity partner of chart_docs_to_unfile — how many actually left the prior chart. */
  chart_docs_unfiled: number;
  verification_rows_removed: number;
  register_cleared: boolean;
  /** Staged/claimed delivery rows recalled or cancelled by the clear (the withdraw-cascade mirror). */
  deliveries_stopped: number;
  /** Their never-transmitted packet PDFs, voided. */
  staged_artifacts_voided: number;
}

// D7 — the per-request timeout for the records SECURE-LINK send, and only that call. Exported so a
// test can pin the number without reading it back out of a source string; the reason it is 120 s
// (and why the global 30 s stays 30 s) is stated at triggerRecordsDelivery below.
export const SECURE_LINK_SEND_TIMEOUT_MS = 120000;

// Tasks
export const tasksApi = {
  getAll: (params?: unknown) => apiClient.get('/tasks', { params }),
  getById: (id: string) => apiClient.get(`/tasks/${id}`),
  create: (data: unknown) => apiClient.post('/tasks', data),
  update: (id: string, data: unknown) => apiClient.put(`/tasks/${id}`, data),
  updateStatus: (id: string, status: string, opts?: { waiting_on?: string; follow_up_at?: string; reason?: string }) =>
    apiClient.patch(`/tasks/${id}/status`, { status, ...(opts || {}) }),
  updateOutcome: (id: string, data: unknown) =>
    apiClient.patch(`/tasks/${id}/outcome`, data),
  // G6 "Extract from fax" (Call-2) — kick off Textract OCR + field extraction over
  // the task's linked PDF, then apply the human-reviewed values into custom/outcome
  // fields. X-Practice-ID pinning is handled by the interceptor; nothing extra here.
  // G7 doc-picker — extract from a chosen document/fax (or all), else the newest linked PDF.
  extractFields: (id: string, source?: { source_type: 'document' | 'fax'; source_id: string } | { all: true }) =>
    apiClient.post(`/tasks/${id}/extract-fields`, source ?? {}),
  extractSources: (id: string) => apiClient.get(`/tasks/${id}/extract-sources`),
  applyExtracted: (id: string, data: unknown) => apiClient.post(`/tasks/${id}/apply-extracted`, data),
  // G7 — clear the "new fax filed" badge; undo a mis-filed attach (returns the fax to the inbox).
  markFaxReviewed: (id: string) => apiClient.post(`/tasks/${id}/fax-reviewed`, {}),
  undoFaxAttach: (id: string, faxId: string) => apiClient.post(`/tasks/${id}/undo-fax-attach`, { fax_id: faxId }),
  // P8 — task-initiated patient correction. PREVIEW classifies the fax's impact graph +
  // returns a TOCTOU graph_hash token; correctPatient re-checks it under lock and cascades.
  correctPatientPreview: (id: string, patientId: string) =>
    apiClient.post(`/tasks/${id}/correct-patient/preview`, { patient_id: patientId }),
  correctPatient: (id: string, data: { patient_id: string; graph_hash: string; reason: string }) =>
    apiClient.post(`/tasks/${id}/correct-patient`, data),
  // P8-C — the CLEAR arm of the same correction pair, discriminated by an explicit clear:true (the
  // swap bodies above are unchanged; a patient-less swap still 400s PATIENT_REQUIRED server-side).
  // Preview returns ClearPreview (counts + a previewed `blocked` refusal); the apply re-checks
  // graph_hash under lock and runs the whole cleanup in ONE audited transaction.
  clearPatientPreview: (id: string) =>
    apiClient.post(`/tasks/${id}/correct-patient/preview`, { clear: true }),
  clearPatient: (id: string, data: { graph_hash: string; reason: string }) =>
    apiClient.post(`/tasks/${id}/correct-patient`, { clear: true, ...data }),
  // Duplicate-task marking — mark THIS task a duplicate of the canonical (survivor):
  // a live task is superseded, an already-closed task is annotated (status kept).
  // unmark reverses (reopens a superseded task, clears the link). The duplicate_of /
  // duplicate_count facets ride on the task detail + list payloads — no read helper.
  markTaskDuplicate: (id: string, canonicalTaskId: string) =>
    apiClient.post(`/tasks/${id}/mark-duplicate`, { canonical_task_id: canonicalTaskId }),
  unmarkTaskDuplicate: (id: string) =>
    apiClient.post(`/tasks/${id}/unmark-duplicate`, {}),
  // assigned_to: a user UUID, or null to UNASSIGN (return the task to its queue).
  // Phase-2 queue board: the assign ROUTE is ADVISORY — an out-of-claim-scope
  // assignee is ALLOWED (the server writes an audited warning), never a hard 400;
  // the assign MENU does the advisory greying. null is used by the Undo of a claim
  // /assign (re-queue) composed client-side from the inverse endpoint.
  assign: (id: string, userId: string | null) =>
    apiClient.patch(`/tasks/${id}/assign`, { assigned_to: userId }),
  // Phase-2 queue board — claim-scope-aware assignee list for ONE task. Returns
  // eligible (sorted lightest open_count first) + ineligible (reason per member).
  getEligibleClaimers: (id: string) =>
    apiClient.get(`/tasks/${id}/eligible-claimers`),
  // Phase A2 follow-up #7 — team_lead reassigns a task within their reports
  // chain (cross-practice OK as long as the target is at the task's practice).
  reassign: (id: string, userId: string) =>
    apiClient.patch(`/tasks/${id}/reassign`, { assigned_to_user_id: userId }),
  // F3 — QUEUE claim semantics. Claim atomically takes an unclaimed queue task;
  // release returns it. releaseAll is the out-of-office sweep (self or, for a
  // manager/team_lead, a target user). getQueueCounts feeds the tab badges.
  claim: (id: string) => apiClient.patch(`/tasks/${id}/claim`),
  release: (id: string) => apiClient.patch(`/tasks/${id}/release`),
  releaseAll: (userId?: string) =>
    apiClient.post('/tasks/release-all', userId ? { user_id: userId } : {}),
  getQueueCounts: (params?: unknown) => apiClient.get('/tasks/queue-counts', { params }),
  getChildren: (id: string) => apiClient.get(`/tasks/${id}/children`),
  addChild: (id: string, data: unknown) =>
    apiClient.post(`/tasks/${id}/children`, data),
  // F8 — procedure-readiness CASE: create + the rolled-up requirement checklist.
  getReadiness: (id: string) => apiClient.get(`/tasks/${id}/readiness`),
  createReadinessCase: (data: {
    patient_id: string; appointment_id?: string | null; procedure_category: string;
    requirements?: string[]; parent_module_key?: string; title?: string;
  }) => apiClient.post('/tasks/readiness-cases', data),
  getComments: (id: string) => apiClient.get(`/tasks/${id}/comments`),
  addComment: (id: string, comment: string) =>
    apiClient.post(`/tasks/${id}/comments`, { comment }),
  getAuditLog: (id: string) => apiClient.get(`/tasks/${id}/audit`),
  getDocuments: (id: string) => apiClient.get(`/tasks/${id}/documents`),
  // Records Release Phase A
  getRecordsReleaseTimeline: (id: string) =>
    apiClient.get(`/tasks/${id}/records-release/timeline`),
  // MRR G1 — the per-case communications feed (read-only aggregation of inbound fax, deliveries,
  // secure links, outbound fax, notification emails, and the narrow delivery audit slice).
  getRecordsReleaseCommunications: (id: string) =>
    apiClient.get(`/tasks/${id}/records-release/communications`),
  extendRecordsRelease: (id: string, data: { reason: string }) =>
    apiClient.patch(`/tasks/${id}/records-release/extend`, data),
  // ROI-M 3b — correct the §164.524 request-received date (the clock anchor); recomputes the deadline.
  correctRecordsReleaseReceivedDate: (id: string, data: { request_received_date: string }) =>
    apiClient.patch(`/tasks/${id}/records-release/received-date`, data),
  // ROI-M Phase 4 (D6) — the approving doctor SELF-CLAIMS the open step-2/4 review. Takes the PARENT id;
  // the server finds the open unassigned child. Gated on approver-ness at the practice — NOT the shared
  // assign route, which is canActOnTasks-gated and 403s a pure practice_doctor (the very approver here).
  claimRecordsApproval: (id: string) =>
    apiClient.patch(`/tasks/${id}/records-release/claim-approval`),
  // MRR P1 — change the recipient lane on a records-release PARENT case.
  setRecordsReleaseLane: (id: string, data: { recipient_lane: string }) =>
    apiClient.patch(`/tasks/${id}/records-release/lane`, data),
  // MRR P4 — set the 42 CFR Part 2 (SUD) flag + capture the §2.31 consent the
  // delivery gate requires.
  setRecordsReleasePart2: (
    id: string,
    data: {
      part2_protected: boolean;
      consent?: {
        consent_obtained: boolean;
        consent_date?: string | null;
        consent_scope?: string | null;
        recipient_name?: string | null;
        purpose?: string | null;
        expires_on?: string | null;
        consent_document_id?: string | null;
      };
    },
  ) => apiClient.patch(`/tasks/${id}/records-release/part2`, data),
  // MRR P3 — Delivery & documented fulfillment. List the live delivery status,
  // trigger a tracked delivery (fax | secure_link), and flag misdirection.
  getRecordsDeliveries: (id: string) =>
    apiClient.get(`/tasks/${id}/records-release/deliveries`),
  // Recovery: start the 6-step workflow (spawn Step 1) on a case that never got one (idempotent).
  startRecordsRelease: (id: string) =>
    apiClient.post(`/tasks/${id}/records-release/start`, {}),
  // MP1 — convert a single records_release case (a bulk pull-list the AI couldn't enumerate) into a
  // multi-patient batch aggregator, in place; the drawer then re-renders as the batch chooser.
  convertRecordsReleaseToBatch: (id: string) =>
    apiClient.post(`/tasks/${id}/records-release/convert-to-batch`, {}),
  // R1.1 Phase 3 — records CHASE (the outbound request-and-chase workflow, records_intake).
  createRecordsChase: (data: {
    patient_id: string; appointment_id?: string | null; sub_category: string;
    requested_provider: { name: string; fax_number?: string | null };
    surgery_date?: string | null; source_leaf_task_id?: string | null; lead_business_days?: number;
  }) => apiClient.post(`/tasks/records-chase`, data),
  // View-before-send Phase B — the chase send is a PREPARE → VIEW → CONFIRM split: prepare
  // builds + stores the request letter WITHOUT parking the step or dispatching, returning
  // { data: { letter_document_id, page_count } } (page_count = the stored letter's server-
  // authoritative count). View it via documentsApi.viewStream, then send with
  // { letter_document_id, pages_seen } — the dispatch key is chase:<stepId>:<letter_document_id>,
  // so a double-click of the SAME letter rides the duplicate path (200 with duplicate:true inside
  // data) while a deliberate follow-up (a fresh prepare = a NEW letter = a new key) stays a legit
  // re-send. pages_seen mismatch ⇒ 409 PAGES_NOT_VIEWED with expected_pages; render_fallback only
  // on the §8.9 new-tab attestation path.
  recordsChasePrepare: (id: string) =>
    apiClient.post(`/tasks/${id}/records-chase/prepare`, {}),
  recordsChaseSend: (
    id: string,
    data?: {
      connection_id?: string | null;
      letter_document_id?: string;
      pages_seen?: number;
      render_fallback?: boolean;
    },
  ) => apiClient.post(`/tasks/${id}/records-chase/send`, data || {}),
  recordsChaseReceived: (id: string, data?: { fax_id?: string | null }) =>
    apiClient.post(`/tasks/${id}/records-chase/received`, data || {}),
  recordsChaseFile: (id: string, data: { fax_id: string; patient_id?: string | null }) =>
    apiClient.post(`/tasks/${id}/records-chase/file`, data),
  recordsChaseFlagForDoctor: (id: string) =>
    apiClient.post(`/tasks/${id}/records-chase/flag-for-doctor`, {}),
  recordsChaseClose: (id: string) =>
    apiClient.post(`/tasks/${id}/records-chase/close`, {}),
  recordsChaseCancel: (id: string) =>
    apiClient.post(`/tasks/${id}/records-chase/cancel`, {}),
  recordsChaseSetProvider: (id: string, data: { name?: string; fax_number?: string | null }) =>
    apiClient.post(`/tasks/${id}/records-chase/provider`, data),
  recordsChasePendingArrival: (id: string) =>
    apiClient.get(`/tasks/${id}/records-chase/pending-arrival`),
  // Draft packet — assemble a merged preview (Step 3) the doctor reviews before approving (Step 4).
  getRecordsDraftPacket: (id: string) =>
    apiClient.get(`/tasks/${id}/records-release/draft-packet`),
  buildRecordsDraftPacket: (id: string) =>
    apiClient.post(`/tasks/${id}/records-release/draft-packet`, {}),
  // MRR drawer Phase-1 — request-details (intake) capture. GET derives how_received/request_received
  // from a linked inbound fax for any still-missing key; PATCH is MERGE-ONLY (absent key untouched).
  getRecordsReleaseIntake: (id: string) =>
    apiClient.get(`/tasks/${id}/records-release/intake`),
  updateRecordsReleaseIntake: (
    id: string,
    data: {
      records_requested?: string;
      date_range_from?: string;
      date_range_to?: string;
      record_types?: string[] | string;
      request_received?: string;
      how_received?: string;
      requestor_contact?: string;
    },
  ) => apiClient.patch(`/tasks/${id}/records-release/intake`, data),
  // MRR drawer Phase-1 — the packet SOURCE docs. The released packet IS exactly these uploaded
  // PDFs in upload order (legacy every-attached-PDF merge only as a fallback when none exist).
  uploadRecordsReleaseDoc: (id: string, formData: FormData) =>
    apiClient.post(`/tasks/${id}/records-release/upload-records`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getRecordsReleaseSourceDocs: (id: string) =>
    apiClient.get(`/tasks/${id}/records-release/source-docs`),
  // ROI-M Phase 2 — bind an operator-uploaded doc as the case's request SOURCE on the PARENT
  // (the Phase-1 route; server-side Step-1-locked ⇒ 409 SOURCE_LOCKED). Mirrors setOnsiteRequestDocument.
  setRecordsReleaseRequestDocument: (id: string, document_id: string, via?: string) =>
    apiClient.post(`/tasks/${id}/records-release/request-document`, { document_id, via }),
  // MRR operator-feedback A — packet source-doc REMOVE + REORDER. The server is authoritative:
  // both 409 PACKET_ALREADY_DELIVERED once a non-failed delivery exists. Reorder sends the FULL
  // ordered id list (the backend intersects it against the case's real docs).
  recordsReleaseSourceDocDelete: (id: string, docId: string) =>
    apiClient.delete(`/tasks/${id}/records-release/source-docs/${docId}`),
  recordsReleaseSourceDocsReorder: (id: string, ordered_ids: string[]) =>
    apiClient.post(`/tasks/${id}/records-release/source-docs/reorder`, { ordered_ids }),
  // MRR drawer Phase-1 — re-fire the doctor notification for the OPEN step-2/4 child to the current
  // assignee (or the approver pool if unassigned) WITHOUT changing the assignment.
  resendRecordsReleaseNotification: (id: string) =>
    apiClient.post(`/tasks/${id}/records-release/resend-notification`, {}),
  // View-before-send Phase B — the FAX deliver is now a PREPARE → VIEW → CONFIRM split
  // (secure_link is UNCHANGED: no fax, no gate). prepare-fax runs the same gate stack +
  // buildArtifact as the deliver route but creates the delivery row as status='prepared'
  // WITHOUT dispatching (a re-prepare first supersedes any prior 'prepared' delivery for the
  // task — F3), returning { data: { delivery_id, artifact_document_id, page_count } }
  // (page_count = the merged artifact's server-authoritative count — the number the confirm
  // validates pages_seen against). The recipient (number/name) is FROZEN on the prepared row
  // here — the confirm carries none. A re-send is a fresh prepare → a fresh delivery row → a
  // fresh dispatch key; the re_send audit marker rides the CONFIRM body, not this prepare.
  prepareRecordsFaxDelivery: (
    id: string,
    data: {
      recipient_confirmed: true;
      recipient_name?: string;
      to_number: string;
      min_necessary_attested?: boolean; // F8 — required for a fast-lane HCO (payer operations) release
    },
  ) => apiClient.post(`/tasks/${id}/records-release/prepare-fax`, data),
  // View-before-send Phase B — same-origin byte stream of the PREPARED delivery artifact for
  // the in-app viewer (documents ACL chain + a PHI-free view audit; §8.6 no-store headers;
  // practice + task + delivery pinned). Returns { blob, pageCount? }: pageCount is parsed from
  // the X-Page-Count response header (the server's pdf-lib count — may back the pane's §8.9
  // fallback attestation); header absent/invalid ⇒ undefined (render-only arming).
  recordsDeliveryArtifactStream: async (taskId: string, deliveryId: string): Promise<{ blob: Blob; pageCount?: number }> => {
    const r = await apiClient.get(`/tasks/${taskId}/records-release/deliveries/${deliveryId}/artifact/stream`, { responseType: 'blob' });
    const n = Number((r.headers as Record<string, unknown>)?.['x-page-count']);
    return { blob: r.data as Blob, pageCount: Number.isInteger(n) && n >= 1 ? n : undefined };
  },
  triggerRecordsDelivery: (
    id: string,
    data:
      // View-before-send Phase B (method='fax') — the CONFIRM half of prepare→view→send. The
      // recipient (number/name/lane) was FROZEN on the prepared row at prepare-fax time — the
      // confirm carries NO recipient fields. delivery_id = the 'prepared' row from
      // prepareRecordsFaxDelivery (the server claims it atomically: 'prepared'→'pending';
      // missing/expired(24h)/superseded/already-claimed ⇒ 409 PREPARE_REQUIRED — re-prepare,
      // re-view, confirm again). pages_seen must equal the STORED artifact's page count (409
      // PAGES_NOT_VIEWED with expected_pages otherwise); render_fallback only on the §8.9
      // new-tab attestation path. The server transmits the pre-built stored artifact
      // (byte-parity — never rebuilt) under the dispatch key records:<delivery_id>: a retry of
      // the same confirm returns 200 with duplicate:true next to data (honest success); a
      // concurrent confirm mid-dispatch ⇒ 409 SEND_IN_PROGRESS; an indeterminate dispatch ⇒
      // 409 SEND_UNCONFIRMED (never retry blind). The full gate battery re-runs at confirm
      // (§8.11), so the prepare-time 409s can fire here too.
      | {
          method: 'fax';
          delivery_id: string;
          pages_seen: number;
          render_fallback?: boolean;
          re_send?: boolean;         // re-deliver on a completed case (new disclosure; adds an audit marker)
          min_necessary_attested?: boolean; // F8 — required for a fast-lane HCO (payer operations) release
        }
      // secure_link is UNCHANGED — the one-shot build+send path (no fax, no view gate).
      | {
          method: 'secure_link';
          recipient_confirmed: true;
          recipient_name?: string;
          recipient_email: string;
          unsecure_consent?: boolean;
          re_send?: boolean;         // re-deliver on a completed case (new disclosure; adds an audit marker)
          min_necessary_attested?: boolean; // F8 — required for a fast-lane HCO (payer operations) release
        },
  ) => apiClient.post(
    `/tasks/${id}/records-release/deliver`,
    data,
    // D7 — the SECURE-LINK send is the one call on this route that can outrun the 30 s global
    // timeout: it merges the packet from S3 with pdf-lib (seconds on a multi-document 40-page
    // packet) and then, in one transaction, takes the delivery row FOR UPDATE (a concurrent second
    // click WAITS on that lock), rotates the token, writes the §164.528 AoD and sends the email.
    // A client abort here is NOT a no-op: the server may already have committed the disclosure and
    // emailed the link, so the operator's retry is a REAL second disclosure. 120 s matches this
    // file's existing long-request override (onsiteVendorAttestationUpload). The FAX confirm keeps
    // the global 30 s — its packet was already built at prepare time. The GLOBAL default is
    // untouched; this is a per-request override only.
    data.method === 'secure_link' ? { timeout: SECURE_LINK_SEND_TIMEOUT_MS } : undefined,
  ),
  // Record a delivery that already happened OUTSIDE the system (manually faxed / mailed /
  // handed over). Logs the §164.528 AoD with the USER-ENTERED past date + method and SENDS
  // NOTHING. Does NOT block on unmet pre-release gates — the response returns them as `flags`
  // so they can be noted on the compliance record (record-and-flag).
  recordsDeliverManual: (
    id: string,
    data: {
      method: 'fax' | 'mail' | 'in_person' | 'secure_email' | 'vendor_portal';
      disclosure_date: string; // YYYY-MM-DD, <= today
      recipient_name?: string;
      note?: string;
      attestation: true;
      min_necessary_attested?: boolean; // F8 — required for a fast-lane HCO (payer operations) release
    },
  ) => apiClient.post(`/tasks/${id}/records-release/deliver-manual`, data),
  markRecordsDeliveryMisdirected: (id: string, deliveryId: string, data: { reason: string }) =>
    apiClient.post(`/tasks/${id}/records-release/deliveries/${deliveryId}/misdirected`, data),
  // Change a fax-spawned task's MODULE (cascades to the source fax + re-routes the task).
  // `reason` is REQUIRED server-side when moving a records-release request out of the module
  // (Commit C — recorded on the ROI Request Register tombstone).
  changeTaskModule: (id: string, data: { module_id: string; reason?: string }) =>
    apiClient.patch(`/tasks/${id}/module`, data),
  // MRR P5 — requester AUTHORITY (§164.514(h)) capture.
  setRecordsReleaseAuthority: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/tasks/${id}/records-release/authority`, data),
  // MRR P6 — §164.508 authorization capture + validation.
  setRecordsReleaseAuthorization: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/tasks/${id}/records-release/authorization`, data),
  // MRR P8 — lane-aware fee estimate + waiver.
  estimateRecordsReleaseFee: (id: string, data: Record<string, unknown>) =>
    apiClient.post(`/tasks/${id}/records-release/fee/estimate`, data),
  waiveRecordsReleaseFee: (id: string, data: { reason: string }) =>
    apiClient.post(`/tasks/${id}/records-release/fee/waive`, data),
  // ROI Phase 3 — record a COLLECTED fee payment (mirror of waive). collected_cents in CENTS.
  // The fee register then shows collected-vs-estimated for every lane.
  recordFeePayment: (
    id: string,
    data: { collected_cents: number; collected_method: 'check' | 'cash' | 'card' | 'other'; collection_note?: string },
  ) => apiClient.post(`/tasks/${id}/records-release/fee/payment`, data),
  // ROI Phase 3 — STORE-ONLY specialist letters (no fax action). After generating, refresh the
  // case documents list so the new PDF shows; the operator sends it manually.
  // deficiency-letter → 400 if the auth is valid / has no reasons; extension-letter → 400 if no extension granted.
  generateDeficiencyLetter: (id: string) =>
    apiClient.post(`/tasks/${id}/records-release/deficiency-letter`, {}),
  generateExtensionLetter: (id: string) =>
    apiClient.post(`/tasks/${id}/records-release/extension-letter`, {}),
  // MRR P9 — sensitive/DRS review (+ the psychotherapy-notes gate).
  setRecordsReleaseSensitiveReview: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/tasks/${id}/records-release/sensitive-review`, data),
  // MRR P10 — denial workflow (basis + reviewable type + partial grant + independent reviewer).
  setRecordsReleaseDenial: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/tasks/${id}/records-release/denial`, data),
  // MRR P11 — §164.512 required-by-law legal-basis validation.
  setRecordsReleaseLegalBasis: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/tasks/${id}/records-release/legal-basis`, data),
  // MRR P12 — patient-rights cross-checks (§164.526 / §164.522).
  setRecordsReleasePatientRights: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/tasks/${id}/records-release/patient-rights`, data),
  // MRR P13 — breach risk-assessment + notification tracking.
  setRecordsReleaseBreach: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/tasks/${id}/records-release/breach`, data),
  // MRR Stage 2 — the practice's DESIGNATED records approvers (+ clinical-license badges) for the
  // step-1 approver picker, and reassignment of the OPEN doctor-approval step (coverage / stuck case).
  getRecordsEligibleApprovers: (id: string) =>
    apiClient.get(`/tasks/${id}/records-release/eligible-approvers`),
  // ROI-M Phase 4 — expect_unassigned: the Step-4 PICKER (an INITIAL routing) sets it so the assign cannot
  // overwrite an approver who just SELF-CLAIMED the step (409 ALREADY_CLAIMED instead). The coverage-reassign
  // surface omits it and keeps takeover semantics.
  reassignRecordsApprover: (id: string, data: { approver_user_id: string; expect_unassigned?: boolean }) =>
    apiClient.patch(`/tasks/${id}/records-release/reassign`, data),
  // B1 — PER-CASE approval ESCALATION on a COLLAPSED lane: add step 2 (approval review) while Step 1
  // is still open, or step 4 (packet approval) while Step 3 is. The lane mask stays the FLOOR — this
  // only ADDS a doctor step to THIS case. Removing one (enabled:false) is narrow by design: same
  // window, same operator who added it, reason REQUIRED, and never once the step has spawned
  // (422 ALREADY_SPAWNED / ESCALATION_WINDOW_CLOSED / STEP_ALREADY_IN_LANE · 403 NOT_ARMING_OPERATOR).
  setRecordsApprovalEscalation: (
    id: string,
    data: { step: 2 | 4; enabled: boolean; reason?: string },
  ) => apiClient.post(`/tasks/${id}/records-release/escalate-approval`, data),
  // MRR P14 — terminal actions: no-responsive-records close + withdrawal/recall.
  // Operator-feedback C — the close now carries a reason (+ optional note / where_directed), and is
  // paired with the §164.524(b)(2) written response (letter draft/render + fax/manual send below).
  recordsReleaseNoResponsiveRecords: (
    id: string,
    data?: { reason?: string; note?: string; where_directed?: string },
  ) => apiClient.post(`/tasks/${id}/records-release/no-responsive-records`, data || {}),
  // The no-records written response: draft the editable letter text, render+store the PDF, then
  // send it by fax or record a manual response. Draft + letter + response are all callable
  // pre- AND post-terminal (finish the loop late from the closed case card).
  // POST (not GET): operator reason/where-directed must ride the JSON body, not the query string
  // (query strings land in access logs — PHI/operator-text hygiene).
  // B2 — the not-found reasons (patient_not_identified / patient_never_seen) carry three
  // HUMAN-TRANSCRIBED identifiers copied off the request; absent ⇒ the letter renders visible blanks
  // and the STORE call 400s LETTER_INCOMPLETE. attach_original (default OFF) merges the case's inbound
  // request fax behind the letter — the stored document stays the SAME letter shape (name prefix /
  // upload_source / storage key), so the fax gate and the reclaim bound still recognise it.
  recordsReleaseNoRecordsLetterDraft: (
    id: string,
    data?: { reason?: string; where_directed?: string; patient_as_written?: string; dob_as_written?: string; request_dated?: string },
  ) => apiClient.post(`/tasks/${id}/records-release/no-records-letter/draft`, data || {}),
  recordsReleaseNoRecordsLetter: (
    id: string,
    data: {
      body_text: string; reason?: string; attach_original?: boolean;
      patient_as_written?: string; dob_as_written?: string; request_dated?: string;
    },
  ) => apiClient.post(`/tasks/${id}/records-release/no-records-letter`, data),
  // View-before-send Phase B — the fax mode gains the viewer attestation: pages_seen must equal
  // the STORED letter document's page count (single-page letters ⇒ 1; mismatch ⇒ 409
  // PAGES_NOT_VIEWED with expected_pages); render_fallback only on the §8.9 new-tab path. View
  // the letter via documentsApi.viewStream first. Dispatch key = nrr:<taskId>:<document_id>:<fax_to>
  // (server-derived) — a double-click of the SAME letter to the SAME number rides the duplicate path
  // (200 with duplicate:true inside data); a corrected fax number OR a new letter = a new key.
  recordsReleaseNoRecordsResponse: (
    id: string,
    data:
      | { mode: 'fax'; document_id: string; fax_to: string; pages_seen?: number; render_fallback?: boolean }
      | { mode: 'manual'; method: 'fax' | 'mail' | 'phone' | 'portal'; responded_on: string; attestation: true },
  ) => apiClient.post(`/tasks/${id}/records-release/no-records-response`, data),
  // §164.524 "withdrawn" = the REQUESTER withdrew the request, so the capture is MANDATORY and the
  // body is REQUIRED (a body-less call used to be legal and recorded nothing — the server now 400s
  // it, so the type is what stops a new caller shipping that break). `reason` is free text and is
  // deliberately NOT audit-logged; `communication_method` is a fixed enum
  // (phone|fax|email|mail|in_person|portal — WITHDRAW_COMMUNICATION_METHODS in
  // RecordsReleaseDetailPanel) and `communicated_on` an ISO date that may not be in the future.
  recordsReleaseWithdraw: (
    id: string,
    data: { reason: string; communication_method: string; communicated_on: string },
  ) => apiClient.post(`/tasks/${id}/records-release/withdraw`, data),
  // ADMIN-only — reopen a case that was CLOSED IN ERROR (parent done while a step is still open).
  // Resumes the open step; a reason is required. (Practice Administrator capability, server-gated.)
  recordsReleaseReopen: (id: string, data: { reason: string }) =>
    apiClient.post(`/tasks/${id}/records-release/reopen`, data),
  // Records-aware DUPLICATE retire — supersede THIS records-release case in favour of a canonical
  // (surviving) case. AoD-free (nothing was disclosed), audited, and reconciles the HIPAA clock onto
  // the survivor (it keeps the EARLIER received-date + deadline). The "same request" attestation
  // (same patient / requestor / scope) is MANDATORY — the server 422s ATTESTATION_REQUIRED without it.
  // Returns { task_id, outcome:'superseded', canonical_task_id, recalled_deliveries, reconciled_deadline }.
  markRecordsDuplicate: (
    id: string,
    data: { canonical_task_id: string; same_request_attested: true; reason?: string },
  ) => apiClient.post(`/tasks/${id}/records-release/mark-duplicate`, data),
  // ADMIN-only VOID — a records-release case that should never have existed ("logged in error").
  // Cancels the case AoD-free (no disclosure asserted); reversible only by an admin Reopen. A reason
  // is required (400 REASON_REQUIRED). Returns { task_id, outcome_status:'logged_in_error', recalled_deliveries }.
  logRecordsCaseInError: (id: string, data: { reason: string }) =>
    apiClient.post(`/tasks/${id}/records-release/logged-in-error`, data),
  // Match an UNMATCHED (fax-born) records-release case to a patient (Step-3 upload + the §164.528
  // AoD require patient_id). Sets the parent's patient + syncs the source fax. Patient is mandatory.
  recordsReleaseLinkPatient: (id: string, patient_id: string) =>
    apiClient.post(`/tasks/${id}/records-release/link-patient`, { patient_id }),
  // Stage the source fax as the Step-1 signed authorization (fax-born cases — the fax IS the doc).
  recordsReleaseUseFaxAuthorization: (id: string) =>
    apiClient.post(`/tasks/${id}/records-release/use-fax-authorization`, {}),
  // Onsite bulk retrieval (payer §164.506 — vendor pulls onsite).
  getOnsiteRetrieval: (id: string) => apiClient.get(`/tasks/${id}/onsite-retrieval`),
  promoteOnsiteRetrieval: (id: string, data: Record<string, unknown>) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/promote`, data),
  scheduleOnsiteRetrieval: (id: string, data: Record<string, unknown>) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/schedule`, data),
  confirmOnsiteRetrieval: (id: string, data?: Record<string, unknown>) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/confirm`, data || {}),
  // The other operator choice: fan the batch out to one records-release case per patient.
  perPatientOnsiteRetrieval: (id: string) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/per-patient`, {}),
  // Vendor sign-off (onsite chain of custody) — email the rep a link, or record an uploaded PDF.
  sendOnsiteSignOffLink: (id: string, data: { recipient_name?: string; recipient_email: string }) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/sign-off-link`, data),
  recordOnsiteVendorAttestation: (id: string, data: { document_id: string; signer_name?: string }) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/vendor-attestation`, data),
  // Dedicated onsite vendor sign-off upload: multipart in ONE request (stores the signed
  // PDF/scan AND records the attestation server-side — no orphan window). Route-scoped 35MB
  // cap; per-request 120s timeout because a legitimate 25MB signed scan on a slow uplink
  // outruns the global 30s axios timeout.
  onsiteVendorAttestationUpload: (
    id: string,
    file: File,
    repName?: string,
    onProgress?: (pct: number) => void
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    if (repName && repName.trim()) fd.append('rep_name', repName.trim());
    return apiClient.post(`/tasks/${id}/onsite-retrieval/vendor-attestation-upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: onProgress
        ? (e) => onProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0)
        : undefined,
    });
  },
  // Onsite compliance-depth batch: exception disposition, sensitive screening, the visit
  // worksheet PDF, and ⇄ Change path (revert to the chooser).
  dispositionOnsiteException: (id: string, childId: string, data: { disposition: string; patient_id?: string }) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/exceptions/${childId}/disposition`, data),
  // Undo a NEGATIVE verdict (not_pulled / not_our_patient) on an unmatched-name exception — the child
  // reopens; the reason lives on the case. A linked child is never reopened here.
  reopenOnsiteException: (id: string, childId: string, data: { reason: string }) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/exceptions/${childId}/reopen`, data),
  setOnsiteScreening: (id: string, data: { exclusions: Array<{ patient_id: string; reason: string }> }) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/screening`, data),
  getOnsiteWorksheet: (id: string) =>
    apiClient.get(`/tasks/${id}/onsite-retrieval/worksheet`, { responseType: 'blob' }),
  revertOnsitePath: (id: string) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/revert`, {}),
  // P1b — manual multi-patient batch (email/mail/portal intake; Option B case_patient_links).
  createManualRecordsBatch: (data: Record<string, unknown>) =>
    apiClient.post('/tasks/records-release/manual-batch', data),
  addOnsiteManifestPatients: (id: string, patient_ids: string[]) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/manifest/patients`, { patient_ids }),
  addOnsiteManifestException: (id: string, label: string) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/manifest/exception`, { label }),
  setOnsiteRequestDocument: (id: string, document_id: string) =>
    apiClient.post(`/tasks/${id}/onsite-retrieval/request-document`, { document_id }),
};

// Dashboard
export const dashboardApi = {
  getMetrics: (params?: unknown) => apiClient.get('/dashboard/metrics', { params }),
  getTeamMetrics: () => apiClient.get('/dashboard/team'),
};

// Schedules (F9) — recurrence rules that emit tasks (the 4th work primitive).
export const schedulesApi = {
  list: () => apiClient.get('/schedules'),
  create: (data: unknown) => apiClient.post('/schedules', data),
  update: (id: string, data: unknown) => apiClient.patch(`/schedules/${id}`, data),
  remove: (id: string) => apiClient.delete(`/schedules/${id}`),
};

// Queues (F3) — queue + membership administration.
export const queuesApi = {
  list: () => apiClient.get('/queues'),
  members: (queueId: string) => apiClient.get(`/queues/${queueId}/members`),
  // Role-scoped addable users: practice staff always; assigned platform users
  // only when a platform_admin / platform_account_manager curates.
  eligibleMembers: (queueId: string) => apiClient.get(`/queues/${queueId}/eligible-members`),
  addMember: (queueId: string, userId: string) =>
    apiClient.post(`/queues/${queueId}/members`, { user_id: userId }),
  removeMember: (queueId: string, userId: string) =>
    apiClient.delete(`/queues/${queueId}/members/${userId}`),
  // G9 Stage 3b — set a member's per-dimension claim/view sub-category scope.
  // Send ONLY the dimension(s) you changed. Server forces subcats empty for all/none.
  setMemberScope: (
    queueId: string,
    userId: string,
    patch: { claim_scope_mode?: string; claim_subcats?: string[]; view_scope_mode?: string; view_subcats?: string[] },
  ) => apiClient.patch(`/queues/${queueId}/members/${userId}/scope`, patch),
  // T1 — set/clear the ⭐ LEAD flag for a queue member (a lead can cover the module's work).
  setMemberLead: (queueId: string, userId: string, isLead: boolean) =>
    apiClient.patch(`/queues/${queueId}/members/${userId}/lead`, { is_lead: isLead }),
};

// Modules
export const modulesApi = {
  getAll: (includeInactive = false) =>
    apiClient.get('/modules', { params: includeInactive ? { include_inactive: 1 } : {} }),
  getPracticeModules: () => apiClient.get('/modules/practice'),
  toggleModule: (moduleId: string, enabled: boolean) =>
    apiClient.patch(`/modules/${moduleId}/toggle`, { enabled }),
  // G9 Stage 3b — opt a (practice, module) in/out of per-member sub-category scoping.
  setSubcategoryScoping: (moduleId: string, enabled: boolean) =>
    apiClient.patch(`/modules/${moduleId}/subcategory-scoping`, { enabled }),
  // G7 — platform-admin: mark a module response-type (inbound faxes can file as evidence on its open tasks).
  setAcceptsFaxResponse: (moduleId: string, accepts_fax_response: boolean) =>
    apiClient.put(`/modules/${moduleId}/accepts-fax-response`, { accepts_fax_response }),
  // Fax-task close-out: platform-admin — mark a module a "filing module" (its finished
  // fax tasks can file the fax PDF into the matched patient's Documents). mig 106.
  setFilesToDocuments: (moduleId: string, files_to_documents: boolean) =>
    apiClient.patch(`/modules/${moduleId}/filing`, { files_to_documents }),
  // Module DESCRIPTION (founder 2026-06-18) — GLOBAL, platform-admin only. Feeds the
  // AI fax classifier's purpose catalog, so it is editable ONLY here (Platform →
  // Modules), read-only in the practice view. Dedicated route works on SYSTEM
  // modules (Authorization / Referral) too — no is_system filter. Pass null to clear.
  setModuleDescription: (moduleId: string, description: string | null) =>
    apiClient.patch(`/modules/${moduleId}/description`, { description }),
  updateModuleOverrides: (moduleId: string, data: {
    custom_sla_hours?: number | null;
    custom_due_offset_days?: number | null;
  }) => apiClient.put(`/modules/${moduleId}/overrides`, data),
  // Module consolidation (Phase 1) — per-practice SLA / due-offset override for a
  // single sub-category. Gated canConfigurePractice; upserts practice_subcategory_overrides.
  // NOTE: no GET exists yet to read these back, so the drawer treats the inputs as
  // write-only (blank = leave unchanged / clear).
  setSubcategoryOverride: (subcatId: string, data: {
    custom_sla_hours?: number | null;
    custom_due_offset_days?: number | null;
  }) => apiClient.put(`/modules/subcategories/${subcatId}/overrides`, data),
  getModuleHistory: (moduleId: string) =>
    apiClient.get(`/modules/${moduleId}/history`),
  getFields: (moduleId: string) => apiClient.get(`/modules/${moduleId}/fields`),
  createField: (moduleId: string, data: unknown) =>
    apiClient.post(`/modules/${moduleId}/fields`, data),
  updateField: (moduleId: string, fieldId: string, data: unknown) =>
    apiClient.put(`/modules/${moduleId}/fields/${fieldId}`, data),
  deleteField: (moduleId: string, fieldId: string) =>
    apiClient.delete(`/modules/${moduleId}/fields/${fieldId}`),
  // G1: Platform-admin module CRUD (Module = Category unification)
  createModule: (data: Record<string, unknown>) => apiClient.post('/modules', data),
  updateModule: (moduleId: string, data: Record<string, unknown>) =>
    apiClient.put(`/modules/${moduleId}`, data),
  deleteModule: (moduleId: string, confirm?: boolean) =>
    apiClient.delete(`/modules/${moduleId}`, { data: confirm ? { confirm: true } : {} }),
  // T9 — platform-admin module price (cents) with audited reason
  setPrice: (moduleId: string, data: { price_cents: number; reason?: string }) =>
    apiClient.put(`/modules/${moduleId}/price`, data),
  // T9 — per-practice comp waiver
  setComp: (moduleId: string, data: { practice_id: string; comped_free: boolean }) =>
    apiClient.patch(`/modules/${moduleId}/comp`, data),
  // T10 — manifest (weight / required_license declare-only / extras)
  setManifest: (moduleId: string, data: { weight?: 'heavy' | 'thin'; required_license?: string | null; manifest_extras?: Record<string, unknown> }) =>
    apiClient.patch(`/modules/${moduleId}/manifest`, data),
  // T10 — typed manifest projection, keyed by module_KEY (not id)
  getManifest: (moduleKey: string) =>
    apiClient.get(`/modules/${moduleKey}/manifest`),
};

// T8 — platform-admin role catalog (named role bundles → user_grants + users.rank).
// PUT replaces the FULL grant-set; assign is idempotent (fans user_grants + stamps
// users.rank, which is dormant until T8b). Backend is platform_admin-only.
export const roleCatalogApi = {
  getAll: () => apiClient.get('/role-catalog'),
  create: (data: { key: string; name: string; description?: string | null; rank: 'Admin' | 'Manager' | 'Lead' | 'Member' | 'Viewer'; grants?: Array<{ grant_type: 'responsibility' | 'module_admin'; grant_key: string }> }) =>
    apiClient.post('/role-catalog', data),
  update: (id: string, data: { name?: string; description?: string | null; rank?: string; is_active?: boolean; grants?: Array<{ grant_type: string; grant_key: string }> }) =>
    apiClient.put(`/role-catalog/${id}`, data),
  remove: (id: string) => apiClient.delete(`/role-catalog/${id}`),
  assign: (id: string, userId: string) =>
    apiClient.post(`/role-catalog/${id}/assign`, { user_id: userId }),
};

// Platform-admin feature flags — runtime toggles for the two auth-gate flags
// (RANK_GATE_FROM_COLUMN / LICENSE_GATE_FROM_MODULE). Backend is platform_admin-only.
export const featureFlagsApi = {
  getAll: () => apiClient.get('/feature-flags'),
  toggle: (key: string, enabled: boolean) => apiClient.put(`/feature-flags/${key}`, { enabled }),
};

// Imports
export const importsApi = {
  // DRI-F1 (D-2026-10-06-09): the two one-step uploads are PINNED to the practice they were clicked
  // at, like the commits below — a per-request X-Practice-ID wins over the global selection. With no
  // practice selected at the click (null) nothing is pinned and the server answers PRACTICE_REQUIRED.
  uploadAppointments: (formData: FormData, clickedPracticeId: string | null) =>
    apiClient.post('/imports/appointments', formData, {
      headers: { 'Content-Type': 'multipart/form-data', ...(clickedPracticeId ? { 'X-Practice-ID': clickedPracticeId } : {}) },
      timeout: 600000,
    }),
  // Batch 2 (FIG 11) — pre-import dry-run: parse the CSV, forecast tasks by module ×
  // skip-reason, write NOTHING. Reuses the imports multer/parser server-side.
  appointmentsDryRun: (formData: FormData) =>
    apiClient.post('/imports/appointments/dry-run', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    }),
  uploadInsurance: (formData: FormData, clickedPracticeId: string | null) =>
    apiClient.post('/imports/insurance', formData, {
      headers: { 'Content-Type': 'multipart/form-data', ...(clickedPracticeId ? { 'X-Practice-ID': clickedPracticeId } : {}) },
      timeout: 600000,
    }),
  // Patient demographics — two-step: preview (no writes) then commit (file wins)
  // F8: both previews are PINNED to the practice they were clicked at, like the uploads above (the
  // server audits a preview under the request's practice). A null practice at the click pins nothing.
  previewPatients: (formData: FormData, clickedPracticeId: string | null) =>
    apiClient.post('/imports/patients?mode=preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data', ...(clickedPracticeId ? { 'X-Practice-ID': clickedPracticeId } : {}) },
      timeout: 600000,
    }),
  // W1 (D-2026-10-06-01): a commit is PINNED to the practice its preview was made at — the
  // per-request X-Practice-ID wins over the global selection (the request interceptor never
  // overwrites a caller-set one), so a practice switch cannot redirect the file.
  commitPatients: (formData: FormData, previewedPracticeId: string) =>
    apiClient.post('/imports/patients?mode=commit', formData, {
      headers: { 'Content-Type': 'multipart/form-data', 'X-Practice-ID': previewedPracticeId },
      timeout: 600000, // a 40k-row commit runs for minutes; 30s made the browser give up while the server kept going
    }),
  // Unified import (Phase 2 orchestrator) — one file: patients+COB pass, then appointments pass
  getUnifiedStatus: () => apiClient.get('/imports/unified/status'),
  previewUnified: (formData: FormData, clickedPracticeId: string | null) =>
    apiClient.post('/imports/unified?mode=preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data', ...(clickedPracticeId ? { 'X-Practice-ID': clickedPracticeId } : {}) },
      timeout: 600000, // large files take minutes — the global 30s timeout aborted the WAIT, not the work
    }),
  commitUnified: (formData: FormData, previewedPracticeId: string) =>
    apiClient.post('/imports/unified?mode=commit', formData, {
      headers: { 'Content-Type': 'multipart/form-data', 'X-Practice-ID': previewedPracticeId },
      timeout: 600000,
    }),
  rewindImport: (id: string) => apiClient.post(`/imports/${id}/rewind`),
  revertImport: (id: string) => apiClient.post(`/imports/${id}/revert`),
  getImportFileUrl: (id: string) => apiClient.get(`/imports/${id}/file`),
  // F1 — appointment reconciliation review (suspected absence-cancellations)
  listSuspectedCancellations: () => apiClient.get('/imports/reconcile/suspected'),
  confirmCancel: (appointmentId: string) => apiClient.post(`/imports/reconcile/${appointmentId}/confirm`),
  keepAppointment: (appointmentId: string) => apiClient.post(`/imports/reconcile/${appointmentId}/keep`),
  restoreAppointment: (appointmentId: string) => apiClient.post(`/imports/reconcile/${appointmentId}/restore`),
  getHistory: (params?: unknown) => apiClient.get('/imports', { params }),
  getDetail: (id: string) => apiClient.get(`/imports/${id}`),
  getById: (id: string) => apiClient.get(`/imports/${id}`),
  downloadErrors: (id: string) =>
    apiClient.get(`/imports/${id}/errors`, { responseType: 'blob' }),
};

// Insurance mapping
export const insuranceMappingApi = {
  getPending: () => apiClient.get('/insurance-mapping/pending'),
  getVerified: () => apiClient.get('/insurance-mapping/verified'),
  verify: (mappingId: string, insuranceMasterId: string) =>
    apiClient.post(`/insurance-mapping/${mappingId}/verify`, { insurance_master_id: insuranceMasterId }),
  getMaster: () => apiClient.get('/insurance-mapping/master'),
  requestNewPayer: (mappingId: string) =>
    apiClient.post(`/insurance-mapping/${mappingId}/request-new-payer`),
};

// Insurance registry (global payer registry — platform_admin only)
export const insuranceRegistryApi = {
  getAll: () => apiClient.get('/insurance-registry'),
  create: (data: unknown) => apiClient.post('/insurance-registry', data),
  update: (id: string, data: unknown) => apiClient.patch(`/insurance-registry/${id}`, data),
};

// Platform-admin MRR reference data (state overlays · §2.32 notice · lane policy).
export const mrrReferencesApi = {
  getStateOverlays: () => apiClient.get('/mrr-references/state-overlays'),
  updateStateOverlay: (stateCode: string, data: Record<string, unknown>) =>
    apiClient.patch(`/mrr-references/state-overlays/${stateCode}`, data),
  getPart2Notice: () => apiClient.get('/mrr-references/part2-notice'),
  setPart2Notice: (data: { text: string | null }) => apiClient.patch('/mrr-references/part2-notice', data),
  getLanePolicies: () => apiClient.get('/mrr-references/lane-policies'),
};

// Documents — backend returns signed URLs as JSON from both /view and
// /download (not blobs). View uses `inline` Content-Disposition (renders in
// the browser), download uses `attachment` (triggers browser save). The
// component fetches the URL then either window.open()s or programmatic-
// anchor-clicks to save.
//
// upload() accepts an optional onProgress callback for progress bars.
// Returns 0-100 as an integer; falls back to 0 if Content-Length is unknown.
export const documentsApi = {
  getByTask: (taskId: string) => apiClient.get(`/documents/task/${taskId}`),
  upload: (
    taskId: string,
    formData: FormData,
    onProgress?: (pct: number) => void
  ) =>
    apiClient.post(`/documents/upload/${taskId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => {
            const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            onProgress(pct);
          }
        : undefined,
    }),
  getSignedUrl: (documentId: string, practiceId?: string) =>
    apiClient.get(`/documents/${documentId}/view`,
      practiceId ? { headers: { 'X-Practice-ID': practiceId } } : undefined),
  // View-before-send Phase B — same-origin BYTE stream of a stored document for the in-app
  // FaxPreviewPane (the NRR / records-chase letter view gate). PDF-only (400 NOT_PDF — this is
  // the fax view gate's read, not a generic byte endpoint). Same documents ACL chain as the
  // JSON /view route + the same view audit; §8.6 headers (Cache-Control: private, no-store +
  // inline). Returns { blob, pageCount? }: pageCount from the X-Page-Count response header (the
  // server's pdf-lib count — the number the gated send validates pages_seen against); header
  // absent/invalid ⇒ undefined (the viewer's own render is then the only counting path).
  viewStream: async (documentId: string): Promise<{ blob: Blob; pageCount?: number }> => {
    const r = await apiClient.get(`/documents/${documentId}/stream`, { responseType: 'blob' });
    const n = Number((r.headers as Record<string, unknown>)?.['x-page-count']);
    return { blob: r.data as Blob, pageCount: Number.isInteger(n) && n >= 1 ? n : undefined };
  },
  download: (documentId: string) =>
    apiClient.get(`/documents/${documentId}/download`),
  delete: (documentId: string) =>
    apiClient.delete(`/documents/${documentId}`),
  // Patient Documents tab — every document on file for a patient (ACL-filtered
  // server-side; bulk-uploaded eligibility proofs are practice-readable).
  getByPatient: (patientId: string) => apiClient.get(`/documents/patient/${patientId}`),
};

// Bulk eligibility-PDF upload (platform-only internal tool): stage a folder of
// `emrid_xxxxx.pdf` files → verify each against the PDF preview → commit.
// Every call pins X-Practice-ID to the page's selected practice so the request
// runs in that practice's RLS context regardless of the global selection.
export const stagingApi = {
  stage: (practiceId: string, formData: FormData, onProgress?: (pct: number) => void) =>
    apiClient.post('/documents/staging', formData, {
      headers: { 'Content-Type': 'multipart/form-data', 'X-Practice-ID': practiceId },
      onUploadProgress: onProgress
        ? (e) => onProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0)
        : undefined,
    }),
  list: (practiceId: string, params?: { batch_id?: string }) =>
    apiClient.get('/documents/staging', {
      params: { practice_id: practiceId, ...(params || {}) },
      headers: { 'X-Practice-ID': practiceId },
    }),
  preview: (practiceId: string, id: string) =>
    apiClient.get(`/documents/staging/${id}/preview`, { headers: { 'X-Practice-ID': practiceId } }),
  // Server polls Textract in-request (~20s max) and answers 202 while OCR is
  // still running — the caller just re-invokes. Longer client timeout to match.
  extract: (practiceId: string, id: string) =>
    apiClient.post(`/documents/staging/${id}/extract`, {}, {
      timeout: 60000,
      headers: { 'X-Practice-ID': practiceId },
    }),
  commit: (practiceId: string, id: string, patientId: string, overrideDocKind?: boolean) =>
    apiClient.post(`/documents/staging/${id}/commit`, {
      patient_id: patientId,
      // Explicit, audited "Save anyway" for files the AI screened as NOT an
      // eligibility document (server 409s without it).
      ...(overrideDocKind ? { override_doc_kind: true } : {}),
    }, {
      headers: { 'X-Practice-ID': practiceId },
    }),
  discard: (practiceId: string, id: string) =>
    apiClient.post(`/documents/staging/${id}/discard`, {}, {
      headers: { 'X-Practice-ID': practiceId },
    }),
};

// Notifications
export const notificationsApi = {
  getAll: () => apiClient.get('/notifications'),
  markRead: (id: string) => apiClient.patch(`/notifications/${id}/read`),
  markAllRead: () => apiClient.patch('/notifications/read-all'),
  getPreferences: () => apiClient.get('/notifications/preferences'),
  updatePreferences: (data: unknown) =>
    apiClient.put('/notifications/preferences', data),
};

// Reports
export const reportsApi = {
  // Compliance registers (practice-wide §164.528) — practice-admin gated server-side.
  getAodRegister: (params?: unknown) => apiClient.get('/reports/aod-register', { params }),
  exportAodRegister: (params?: unknown) => apiClient.get('/reports/aod-register/export', { params, responseType: 'blob' }),
  // ROI Request Register (Commit C) — every records-release REQUEST since classification (distinct
  // from the §164.528 AoD above). Practice-admin gated server-side. Filters: date_from/date_to/status/search.
  getRoiRequestRegister: (params?: unknown) => apiClient.get('/reports/roi-request-register', { params }),
  exportRoiRequestRegister: (params?: unknown) => apiClient.get('/reports/roi-request-register/export', { params, responseType: 'blob' }),
  // P2 — the Records Ops dashboard (statutory-clock queue + KPI/mix widgets, one payload).
  getRecordsOps: (days?: number) => apiClient.get('/reports/records-ops', { params: days ? { days } : {} }),
  // P3 — the four audit-ready MRR registers (practice-admin gated server-side).
  getDenialRegister: (days?: number) => apiClient.get('/reports/denial-register', { params: days ? { days } : {} }),
  getBreachRegister: (days?: number) => apiClient.get('/reports/breach-register', { params: days ? { days } : {} }),
  getFeeRegister: (days?: number) => apiClient.get('/reports/fee-register', { params: days ? { days } : {} }),
  getExtensionRegister: (days?: number) => apiClient.get('/reports/extension-register', { params: days ? { days } : {} }),
  // P8 — CSV export of the four registers (blob; same gate + filters as the view routes).
  exportDenialRegister: (days?: number) => apiClient.get('/reports/denial-register/export', { params: days ? { days } : {}, responseType: 'blob' }),
  exportBreachRegister: (days?: number) => apiClient.get('/reports/breach-register/export', { params: days ? { days } : {}, responseType: 'blob' }),
  exportFeeRegister: (days?: number) => apiClient.get('/reports/fee-register/export', { params: days ? { days } : {}, responseType: 'blob' }),
  exportExtensionRegister: (days?: number) => apiClient.get('/reports/extension-register/export', { params: days ? { days } : {}, responseType: 'blob' }),
  getAodRequests: (params?: unknown) => apiClient.get('/reports/aod-requests', { params }),
  // F4 — AoD Requests CSV export (blob; mirrors the sibling register exports, practice-admin gated server-side).
  exportAodRequests: (params?: unknown) => apiClient.get('/reports/aod-requests/export', { params, responseType: 'blob' }),
  // P5 (G3) — the closed-case archive (a register-family lens over closed records CASEs). The view is
  // rank≥Manager OR the records_release archive grant; the CSV export is admin-class (10k-row PHI egress).
  getRecordsArchive: (params?: unknown) => apiClient.get('/reports/records-archive', { params }),
  exportRecordsArchive: (params?: unknown) => apiClient.get('/reports/records-archive/export', { params, responseType: 'blob' }),
  getTaskCompletion: (params?: unknown) =>
    apiClient.get('/reports/task-completion', { params }),
  getOverdueTasks: (params?: unknown) =>
    apiClient.get('/reports/overdue-tasks', { params }),
  getTasksByType: (params?: unknown) =>
    apiClient.get('/reports/tasks-by-type', { params }),
  getSlaBreach: (params?: unknown) =>
    apiClient.get('/reports/sla-breach', { params }),
  getTaskAging: (params?: unknown) =>
    apiClient.get('/reports/task-aging', { params }),
  getStaffProductivity: (params?: unknown) =>
    apiClient.get('/reports/staff-productivity', { params }),
  getPatientLevel: (params?: unknown) =>
    apiClient.get('/reports/patient-level', { params }),
  getAppointmentReadiness: (params?: unknown) =>
    apiClient.get('/reports/appointment-readiness', { params }),
  getEligibility: (params?: unknown) =>
    apiClient.get('/reports/eligibility', { params }),
  getAuthorization: (params?: unknown) =>
    apiClient.get('/reports/authorization', { params }),
  getDenialAr: (params?: unknown) =>
    apiClient.get('/reports/denial-ar', { params }),
  getRecallRetention: (params?: unknown) =>
    apiClient.get('/reports/recall-retention', { params }),
  getImportHistory: (params?: unknown) =>
    apiClient.get('/reports/import-history', { params }),
  getNotificationDelivery: (params?: unknown) =>
    apiClient.get('/reports/notification-delivery', { params }),
  getDocumentAudit: (params?: unknown) =>
    apiClient.get('/reports/document-audit', { params }),
  getPlatformActivity: (params?: unknown) =>
    apiClient.get('/reports/platform-activity', { params }),
  getSecurityCompliance: (params?: unknown) =>
    apiClient.get('/reports/security-compliance', { params }),
  getTrialSubscription: (params?: unknown) =>
    apiClient.get('/reports/trial-subscription', { params }),
  export: (reportType: string, format: 'csv' | 'pdf', params?: unknown) =>
    apiClient.get(`/reports/${reportType}/export/${format}`, {
      params,
      responseType: 'blob',
    }),
};

// Global Audit Trail
export const auditApi = {
  getLog: (params?: unknown) => apiClient.get('/audit', { params }),
  getTaskAudit: (params?: unknown) => apiClient.get('/audit/tasks', { params }),
  verifyChain: () => apiClient.get('/audit/verify-chain'),
};

// Column preferences
export const preferencesApi = {
  getColumns: (page: string) => apiClient.get(`/preferences/columns/${page}`),
  saveColumns: (page: string, columns: unknown[]) => apiClient.put(`/preferences/columns/${page}`, { columns }),
};

// Users
// ai.95 — the answer of GET /users/create-access (one side per user type). `reason` is the server's
// plain-language refusal (names no practice); `roles` is the role ceiling for the caller's basis.
export interface CreateAccessSide {
  allowed: boolean;
  basis: 'platform_admin' | 'account_manager' | 'practice_admin' | 'practice_manager' | null;
  roles: string[];
  reason: string | null;
}
export interface CreateAccess { practice: CreateAccessSide; platform: CreateAccessSide }

export const usersApi = {
  getAll: (params?: { role?: string; status?: string; scope?: 'platform' }) => apiClient.get('/users', { params }),
  // Task-owner dropdown: practice users + platform users assigned to this practice, minus read-only roles.
  // Pass the task's practice so a platform user in Platform mode (no selected practice) still gets a list.
  getAssignable: (practiceId?: string) =>
    apiClient.get('/users/assignable', practiceId ? { params: { practice_id: practiceId } } : undefined),
  getById: (id: string) => apiClient.get(`/users/${id}`),
  // ai.95 — may the CALLER add users, and which roles? The server's own create decision, answered to
  // the screen: { practice: { allowed, basis, roles, reason }, platform: { same } } (NOT wrapped in
  // { data }). practice_id is optional and defaults to the selected practice.
  createAccess: (practiceId?: string) =>
    apiClient.get('/users/create-access', practiceId ? { params: { practice_id: practiceId } } : undefined),
  // Track B ③ — accepts `rank` (server derives the singular role) OR a legacy `role` (back-compat).
  create: (data: {
    first_name: string; last_name: string; email: string; login_method?: string;
    user_type?: 'platform_user' | 'practice_user';
    role?: string;
    rank?: 'Admin' | 'Manager' | 'Lead' | 'Member' | 'Viewer';
    practice_id?: string; assignments?: string[];
  }) => apiClient.post('/users', data),
  update: (id: string, data: unknown) => apiClient.put(`/users/${id}`, data),
  // Phase 1.D — partial update (responsibilities, license, scope, module-admin)
  patch: (id: string, data: unknown) => apiClient.patch(`/users/${id}`, data),
  updateRole: (id: string, role: string, roles?: string[]) =>
    apiClient.patch(`/users/${id}/role`, roles ? { role, roles } : { role }),
  // OD-3 direct rank change — the SAME audited PATCH /users/:id/role, body = { rank } only. The backend
  // derives the rank-bearing role via roleFromRank, PRESERVES any clinical role string, and stores
  // rank = rankFromRoles(roles) (union-max), with the last-admin / self-demote / category guards.
  updateRank: (id: string, rank: 'Admin' | 'Manager' | 'Lead' | 'Member' | 'Viewer') =>
    apiClient.patch(`/users/${id}/role`, { rank }),
  // Phase A2 follow-up #7 — set someone's manager (platform_admin only).
  // null clears the edge (top of chain).
  updateReportsTo: (id: string, reports_to_user_id: string | null) =>
    apiClient.patch(`/users/${id}/reports-to`, { reports_to_user_id }),
  // Phase A2 follow-up #7 — direct + transitive reports for the team_lead
  // calling. Returns each report + open task count + training compliance.
  myTeam: () => apiClient.get('/users/me/team'),
  // C7 — make a user a generalist (sees ALL unclaimed work) or restrict them to their queues.
  setGeneralist: (id: string, is_generalist: boolean) =>
    apiClient.patch(`/users/${id}/generalist`, { is_generalist }),
  // UI Part 2 (T7) — granular, individually-audited grant/scope writes. The user editor fires
  // these on each pill toggle (no batch Save); the backend writes one fail-closed audit row per
  // call and dual-checks practice membership. grants = capabilities; scope = reach (replace-all).
  addGrant: (id: string, grant_type: 'responsibility' | 'module_admin' | 'capability', grant_key: string) =>
    apiClient.post(`/users/${id}/grants`, { grant_type, grant_key }),
  revokeGrant: (id: string, grant_type: 'responsibility' | 'module_admin' | 'capability', grant_key: string) =>
    apiClient.delete(`/users/${id}/grants`, { data: { grant_type, grant_key } }),
  setScope: (id: string, scope_type: 'location' | 'module', scope_values: string[]) =>
    apiClient.put(`/users/${id}/scope`, { scope_type, scope_values }),
  deactivate: (id: string) => apiClient.patch(`/users/${id}/deactivate`),
  activate: (id: string) => apiClient.patch(`/users/${id}/activate`),
  unlock: (id: string) => apiClient.patch(`/users/${id}/unlock`),
  resendInvite: (id: string) => apiClient.post(`/users/${id}/resend-invite`),
  resetPassword: (id: string) => apiClient.post(`/users/${id}/reset-password`),
  // Phase A2 — platform_user_assignments CRUD
  getAssignments: (id: string) => apiClient.get(`/users/${id}/assignments`),
  addAssignment: (id: string, practice_id: string) =>
    apiClient.post(`/users/${id}/assignments`, { practice_id }),
  removeAssignment: (id: string, practice_id: string) =>
    apiClient.delete(`/users/${id}/assignments/${practice_id}`),
  // Org-context (read-only) — the Rekha platform staff assigned to the caller's practice.
  getPlatformStaffForPractice: () => apiClient.get('/users/platform-staff-for-practice'),
  // Wave 3d Staff Coverage cockpit (platform_admin only) — every running practice × its active
  // platform-staff assignments (flat rows; zero-coverage practices included). Name+role only, no PHI.
  staffCoverage: () => apiClient.get('/users/staff-coverage'),
};

// Hierarchy (Phase 1.D — Org/Location/Department CRUD)
export const hierarchyApi = {
  // Organizations
  listOrganizations: () => apiClient.get('/hierarchy/organizations'),
  getOrganization: (id: string) => apiClient.get(`/hierarchy/organizations/${id}`),
  // Org-context (read-only) — sibling practices in an org (own org only for a practice user).
  listOrgPractices: (orgId: string) => apiClient.get(`/hierarchy/organizations/${orgId}/practices`),
  createOrganization: (data: unknown) => apiClient.post('/hierarchy/organizations', data),
  updateOrganization: (id: string, data: unknown) =>
    apiClient.put(`/hierarchy/organizations/${id}`, data),
  // Locations
  listLocations: (practiceId: string) =>
    apiClient.get(`/hierarchy/practices/${practiceId}/locations`),
  createLocation: (practiceId: string, data: unknown) =>
    apiClient.post(`/hierarchy/practices/${practiceId}/locations`, data),
  updateLocation: (id: string, data: unknown) =>
    apiClient.put(`/hierarchy/locations/${id}`, data),
  deleteLocation: (id: string) => apiClient.delete(`/hierarchy/locations/${id}`),
  // Department CRUD removed (department-axis purge Phase A, 2026-06-25).
};

// Billing
export const billingApi = {
  getSubscription: () => apiClient.get('/billing/subscription'),
  getPaymentHistory: () => apiClient.get('/billing/payments'),
  createCheckoutSession: (planType: string) =>
    apiClient.post('/billing/checkout', { plan_type: planType }),
  cancelSubscription: () => apiClient.post('/billing/cancel'),
  updateCard: () => apiClient.post('/billing/update-card'),
  getAllPractices: () => apiClient.get('/billing/practices'), // platform admin only
};

// API Keys (external integration)
export const apiKeysApi = {
  getAll: () => apiClient.get('/api-keys'),
  create: (data: unknown) => apiClient.post('/api-keys', data),
  revoke: (id: string) => apiClient.delete(`/api-keys/${id}`),
};

// Trusted devices (app-level device trust)
export const devicesApi = {
  verifyStart: () => apiClient.post('/devices/verify/start'),
  verifyConfirm: (otp: string) => apiClient.post('/devices/verify/confirm', { otp }),
  list: () => apiClient.get('/devices'),
  revoke: (id: string) => apiClient.delete(`/devices/${id}`),
};

// Skip Rules
export const skipRulesApi = {
  getPracticeRules: (practiceId: string) => apiClient.get(`/practices/${practiceId}/skip-rules`),
  updateRule: (practiceId: string, field: string, data: unknown) =>
    apiClient.put(`/practices/${practiceId}/skip-rules/${field}`, data),
  addRule: (practiceId: string, data: unknown) =>
    apiClient.post(`/practices/${practiceId}/skip-rules`, data),
  resetRule: (practiceId: string, field: string) =>
    apiClient.post(`/practices/${practiceId}/skip-rules/${field}/reset`, {}),
  toggleRule: (practiceId: string, field: string) =>
    apiClient.patch(`/practices/${practiceId}/skip-rules/${field}/toggle`, {}),
  deleteRule: (practiceId: string, field: string) =>
    apiClient.delete(`/practices/${practiceId}/skip-rules/${field}`),
  // Platform admin
  getEmrDefaults: () => apiClient.get('/emr-defaults'),
  getEmrDefaultsByName: (emrName: string) => apiClient.get(`/emr-defaults/${emrName}`),
  updateEmrDefault: (emrName: string, field: string, data: unknown) =>
    apiClient.put(`/emr-defaults/${emrName}/${field}`, data),
  pushEmrDefaults: (emrName: string, field?: string) =>
    apiClient.post(`/emr-defaults/${emrName}/push`, field ? { field } : {}),
};

// EMR Library — platform-admin master EMR config catalog (emr_config_defaults).
// The column/value mappings a practice inherits when it picks an EMR. Distinct
// from skipRulesApi's `/emr-defaults` (those are per-EMR skip-rule defaults).
export const emrLibraryApi = {
  list: () => apiClient.get('/emr-library'),
  create: (data: {
    emr_name: string;
    column_mapping: Record<string, unknown>;
    value_mapping: Record<string, unknown>;
    patient_column_mapping?: Record<string, unknown>;
    description?: string;
  }) => apiClient.post('/emr-library', data),
  update: (emrName: string, data: {
    column_mapping: Record<string, unknown>;
    value_mapping: Record<string, unknown>;
    patient_column_mapping?: Record<string, unknown>;
    description?: string;
  }) => apiClient.put(`/emr-library/${encodeURIComponent(emrName)}`, data),
  remove: (emrName: string) => apiClient.delete(`/emr-library/${encodeURIComponent(emrName)}`),
};

// Registration
export const registerApi = {
  checkEmail: (email: string) => apiClient.post('/register/check-email', { email }),
  verifyNpi: (npi: string) => apiClient.post('/register/verify-npi', { npi }),
  sendOtp: (mobile: string, email: string) => apiClient.post('/register/send-otp', { mobile, email }),
  verifyOtp: (email: string, otp: string) => apiClient.post('/register/verify-otp', { email, otp }),
  complete: (data: unknown) => apiClient.post('/register/complete', data),
  clearDemo: (practice_id: string) => apiClient.post('/register/clear-demo', { practice_id }),
};

// Appointment type mapping
export interface ApptTypeVerifyPayload {
  canonical_type: string;
  category?: string | null;
  module_flags?: Record<string, boolean>;
  description?: string | null; // Batch 3 — the plain-English "Meaning" column.
}
// Batch 3 — FIG 1 catalog overview shape (grouped rows + counts + task-volume aggregate).
export interface ApptTypeOverviewRow {
  mapping_id: string;
  emr_name: string;
  canonical_type: string | null;
  category: string | null;
  description: string | null;
  module_flags: Record<string, boolean>;
  status: string;
  appointment_count: number;   // total appointments on file for this type (0 = none imported yet)
  scheduled_count: number;     // spawn-eligible subset (the per-row task-volume multiplier)
}
export interface ApptTypeOverview {
  rows: ApptTypeOverviewRow[];
  hold_unclassified_appt_types: boolean; // read-only mode indicator (founder flips per practice)
  aggregate: {
    projected_total: number;      // tasks for the appointments on file WITH this config
    projected_fail_open: number;  // tasks if every type were left unclassified (fail-open baseline)
    avoided_pct: number;          // (fail_open − total) / fail_open, rounded
    by_module: { eligibility: number; authorization: number; referral_management: number };
  };
}
export const appointmentTypeMappingApi = {
  getPending: () => apiClient.get('/appointment-type-mapping/pending'),
  getVerified: () => apiClient.get('/appointment-type-mapping/verified'),
  overview: () => apiClient.get('/appointment-type-mapping/overview'),
  verify: (mappingId: string, payload: ApptTypeVerifyPayload) =>
    apiClient.post(`/appointment-type-mapping/${mappingId}/verify`, payload),
};

// Batch 4 — Payer Rules surface (FIGs 6-8). Batch 5 (2026-07-18): the task-creation
// engine now READS practice_payer_rules — enforcement = the module is ON for this
// practice (practice_modules.is_enabled) AND the row is CONFIRMED (payerGate.ts).
// An unconfirmed or missing row still fails safe, exactly like before. This is the
// INSURANCE axis's policy sibling to appointmentTypeMappingApi above (the TYPE axis).
export interface PayerRuleUpsertPayload {
  plan_class?: string | null;
  requires_referral?: boolean | null;
  requires_auth_surgery?: boolean | null;
  requires_auth_injections?: boolean | null;
  requires_auth_laser?: boolean | null;
  requires_auth_testing?: boolean | null;
  notes?: string | null;
}
export interface PayerRuleRow {
  mapping_id: string;
  raw_name: string;
  canonical_name: string | null;
  rule_id: string | null;
  plan_class: string | null;
  requires_referral: boolean | null;
  requires_auth_surgery: boolean | null;
  requires_auth_injections: boolean | null;
  requires_auth_laser: boolean | null;
  requires_auth_testing: boolean | null;
  notes: string | null;
  confirmed: boolean;
  confirmed_by_name: string | null;
  confirmed_at: string | null;
  appointment_count: number;
}
export interface PayerRuleOverview {
  rows: PayerRuleRow[];
  aggregate: {
    total_payers: number;
    confirmed_count: number;
    unconfirmed_count: number;
    total_appointments: number;
  };
}
export const payerRulesApi = {
  overview: () => apiClient.get('/payer-rules/overview'),
  upsert: (mappingId: string, payload: PayerRuleUpsertPayload) =>
    apiClient.put(`/payer-rules/${mappingId}`, payload),
  confirm: (mappingId: string) => apiClient.post(`/payer-rules/${mappingId}/confirm`),
};

// P6 — Task Fan-out rules (appointment-import task_rules matrix). practice-admin+.
export const taskRulesApi = {
  list: () => apiClient.get('/task-rules'),
  create: (data: { module_id: string; condition: Record<string, string>; sla_hours?: number | null; due_days_offset?: number | null; priority?: string | null; name?: string | null }) =>
    apiClient.post('/task-rules', data),
  update: (id: string, data: { condition: Record<string, string>; sla_hours?: number | null; due_days_offset?: number | null; priority?: string | null; name?: string | null }) =>
    apiClient.put(`/task-rules/${id}`, data),
  toggle: (id: string, isActive: boolean) => apiClient.patch(`/task-rules/${id}/toggle`, { is_active: isActive }),
  remove: (id: string) => apiClient.delete(`/task-rules/${id}`),
};

// Batch 2 — Task-automation explainability (FIGs 9/10/11). All READ-ONLY.
// Simulator = practice-admin+; appointment audit = manager+ (returns counts/ids
// only, no PHI). The dry-run lives on importsApi (it reuses the imports multer +
// CSV parser) — see importsApi.appointmentsDryRun below.
export const taskExplainApi = {
  simulatorInputs: () => apiClient.get('/task-explain/simulator/inputs'),
  simulate: (data: { emr_name?: string; category?: string; payer?: string }) =>
    apiClient.post('/task-explain/simulator/simulate', data),
  appointmentAudit: (appointmentId: string) =>
    apiClient.get(`/task-explain/appointment/${appointmentId}`),
};

// Assignment Rules
export const assignmentRulesApi = {
  getAll: (moduleId?: string) =>
    apiClient.get('/assignment-rules', { params: moduleId ? { module_id: moduleId } : {} }),
  create: (data: unknown) => apiClient.post('/assignment-rules', data),
  update: (id: string, data: unknown) => apiClient.patch(`/assignment-rules/${id}`, data),
  delete: (id: string) => apiClient.delete(`/assignment-rules/${id}`),
  reorder: (orderedIds: string[]) => apiClient.post('/assignment-rules/reorder', { ordered_ids: orderedIds }),
  getConditionFields: () => apiClient.get('/assignment-rules/condition-fields'),
};

// Anchor Types — global display config for the 5 task anchor types.
// GET is available to every authenticated role. PUT is platform_admin only
// (enforced server-side).
export const anchorTypesApi = {
  getAll: () => apiClient.get('/anchor-types'),
  update: (anchorKey: string, data: {
    label?: string;
    badge_bg_color?: string;
    badge_text_color?: string;
    icon?: string;
    show_badge?: boolean;
    display_order?: number;
  }) => apiClient.put(`/anchor-types/${anchorKey}`, data),
};

// ─── ONBOARDING WIZARD (Phase 2.A backend / 2.B frontend) ─────────────────────
// Endpoints powering the new HIPAA-aware onboarding wizard:
//   GET  /onboarding/state               — current wizard state + progress
//   POST /onboarding/step/:stepKey       — submit one step's answers
//   POST /onboarding/finalize            — finalize, generate binder, sandbox
//   POST /onboarding/reset               — reset (platform_admin only)
//   GET  /onboarding/subprocessors       — PreVisit's subprocessor catalog
export const onboardingApi = {
  getState: () => apiClient.get('/onboarding/state'),
  submitStep: (stepKey: string, answers: Record<string, unknown>) =>
    apiClient.post(`/onboarding/step/${stepKey}`, { answers }),
  finalize: () => apiClient.post('/onboarding/finalize'),
  reset: () => apiClient.post('/onboarding/reset'),
  getSubprocessors: () => apiClient.get('/onboarding/subprocessors'),
  generateSmokeBinder: () => apiClient.post('/onboarding/binder/smoke'),
};

// ─── ACTIVATION (Phase 2.E) ──────────────────────────────────────────────────
// Sandbox → Activation tier flow:
//   1. getStatus: see current activation state
//   2. createCheckout: start Stripe Checkout for $250
//   3. confirmPayment: called after Stripe success URL with session_id
//   4. signBaa: capture click-through BAA + complete activation
export const activationApi = {
  getStatus: () => apiClient.get('/activation/status'),
  createCheckout: () => apiClient.post('/activation/checkout'),
  confirmPayment: (sessionId: string) =>
    apiClient.post('/activation/payment-confirm', { session_id: sessionId }),
  signBaa: (data: { signer_name: string; signer_title?: string; agreed_to_terms: boolean; baa_version?: string }) =>
    apiClient.post('/activation/baa-sign', data),
};

// ─── ENTERPRISE (Phase 2.E) ──────────────────────────────────────────────────
export const enterpriseApi = {
  submitRequest: (data: {
    practice_size?: string;
    desired_baa_changes?: string;
    legal_contact_name?: string;
    legal_contact_email?: string;
    legal_contact_phone?: string;
    preferred_timeline?: string;
    notes?: string;
  }) => apiClient.post('/enterprise/request', data),
  listMyRequests: () => apiClient.get('/enterprise/requests'),
  createCheckout: () => apiClient.post('/enterprise/checkout'),
  // Admin
  adminListRequests: (params?: { status?: string; assigned_to?: string }) =>
    apiClient.get('/enterprise/admin/requests', { params }),
  adminAdvanceState: (
    requestId: string,
    data: {
      status: 'submitted' | 'contacted' | 'baa_drafted' | 'baa_signed' | 'declined';
      internal_notes?: string;
      declined_reason?: string;
      assigned_to_user_id?: string;
    },
  ) => apiClient.post(`/enterprise/admin/requests/${requestId}/state`, data),
};

// ─── PRICING (Phase 2.E) ─────────────────────────────────────────────────────
export const pricingApi = {
  listPlans: () => apiClient.get('/pricing/plans'),
  history: (planKey?: string) =>
    apiClient.get('/pricing/history', { params: planKey ? { plan_key: planKey } : undefined }),
  updatePlan: (
    planKey: string,
    data: { price_cents?: number; currency?: string; is_active?: boolean; change_reason?: string },
  ) => apiClient.put(`/pricing/plans/${planKey}`, data),
};

// ─── BACKFILL (Phase 2.F — post-hoc HIPAA backfill for legacy practices) ─────
// Sibia and any pre-Phase-2 practice in legacy 'active' status without a
// signed BAA need to retroactively complete the HIPAA wizard. This API
// drives detection, pre-fill, and post-hoc finalize (active → activation
// without sandbox detour, payment, or demo wipe).
export const backfillApi = {
  required: () => apiClient.get('/backfill/required'),
  initiate: () => apiClient.post('/backfill/initiate'),
  finalize: (data: { signer_name: string; signer_title?: string; agreed_to_terms: boolean }) =>
    apiClient.post('/backfill/finalize', data),
};

// ─── COMPLIANCE CENTER (Phase 2.G — B+D combo) ───────────────────────────────
// Slim onboarding adopts PreVisit standard policies on the practice's behalf.
// The Compliance Center surfaces every defaulted item and lets the practice
// customize anytime. Each customization writes a new attestation with
// supersedes_attestation_id pointing back to the prior default — full audit
// chain preserved.
export const complianceApi = {
  getState: () => apiClient.get('/compliance/state'),
  customize: (data: {
    type: 'designation' | 'hipaa_acknowledgement' | 'subcontractor_disclosure' | 'risk_analysis';
    subtype?: string | null;
    payload: Record<string, unknown>;
  }) => apiClient.post('/compliance/customize', data),
  regenerateBinder: () => apiClient.post('/compliance/regenerate-binder'),
  // Phase 3 — policy versioning
  policyStatus: () => apiClient.get('/compliance/policy-status'),
  platformPolicyStatus: (templateKey?: string) =>
    apiClient.get('/compliance/policy-status/platform', {
      params: templateKey ? { template_key: templateKey } : undefined,
    }),
  refreshSchedule: () => apiClient.get('/compliance/refresh-schedule'),
  updateCadence: (data: {
    refresh_cadence_months?: number;
    risk_reanalysis_cadence_months?: number;
  }) => apiClient.put('/compliance/refresh-cadence', data),
  runAnnualRefresh: () => apiClient.post('/compliance/run-annual-refresh'),
  runRiskReanalysis: () => apiClient.post('/compliance/run-risk-reanalysis'),
};

// ─── BINDER (Phase 2.G+ — surface generated PDFs) ────────────────────────────
// The HIPAA binder PDFs are generated at finalize/activation/customization
// and stored in S3+KMS. These endpoints surface them so the practice can
// download + share with their lawyer/auditor.
export const binderApi = {
  list: () => apiClient.get('/binder'),
  latestBaa: () => apiClient.get('/binder/baa/latest'),
  download: (documentId: string) => apiClient.get(`/binder/${documentId}/download`),
};

// ─── POLICY UPDATE WIZARD (Phase 3.B) ────────────────────────────────────────
// When PreVisit bumps a binder template's version, practices on the old version
// re-acknowledge here. status → drift report; start/step → resumable progress;
// finalize → regenerate the drifted docs + write re-ack attestations.
export const policyUpdateApi = {
  status: () => apiClient.get('/policy-update/status'),
  start: () => apiClient.post('/policy-update/start'),
  step: (data: { template_key: string; acknowledged: boolean }) =>
    apiClient.put('/policy-update/step', data),
  finalize: (data?: { acknowledged_template_keys?: string[] }) =>
    apiClient.post('/policy-update/finalize', data ?? {}),
};

// ─── TRAINING (Phase 1 backend + Phase 5 UI) ─────────────────────────────────
// Staff training & certification. Backend = migration 015 + routes/training.ts.
// Phase 5 builds the UI; this client wires every endpoint.
export const trainingApi = {
  // Catalog
  modules: () => apiClient.get('/training/modules'),
  module: (id: string) => apiClient.get(`/training/modules/${id}`),

  // Authoring (platform_admin) — edit existing modules' slides + question bank.
  // `modulesAdmin` includes inactive (deprecated) modules; `moduleFull` returns
  // the unsanitized module WITH the answer key (correct_index + explanation) so
  // the editor can populate it. (No create path yet — edit existing only.)
  modulesAdmin: () => apiClient.get('/training/modules?include_inactive=1'),
  moduleFull: (id: string) => apiClient.get(`/training/modules/${id}/full`),
  updateModule: (id: string, data: Record<string, unknown>) =>
    apiClient.put(`/training/modules/${id}`, data),
  toggleModule: (id: string) => apiClient.patch(`/training/modules/${id}/toggle`),

  // Admin — assignments
  assignments: () => apiClient.get('/training/assignments'),
  createAssignment: (data: {
    training_module_id: string;
    assigned_to_user_id?: string;
    assigned_to_role?: string;
    due_date?: string | null;
    recurrence?: 'annual' | 'one_time';
    auto_renew?: boolean;
    notes?: string;
  }) => apiClient.post('/training/assignments', data),
  updateAssignment: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/training/assignments/${id}`, data),
  resetAssignment: (id: string) => apiClient.post(`/training/assignments/${id}/reset`),

  // Staff — self-renew an EXPIRED certification. Ownership-only authz; no body.
  // 201 { renewed: true } minted a successor assignment with a FRESH attempt
  // budget; 200 { renewed: false } means it was already renewed (double-click, or
  // an admin re-assign after the expiry). BOTH are success — read
  // `data.assignment.assignment_id` and launch it either way. The refusals are
  // coded: NOT_EXPIRED · NOT_CERTIFIED · MODULE_INACTIVE · ASSIGNMENT_NOT_FOUND.
  renewAssignment: (id: string) => apiClient.post(`/training/assignments/${id}/renew`),

  // Staff — my training
  myAssignments: () => apiClient.get('/training/my-assignments'),
  myCompletions: () => apiClient.get('/training/my-completions'),
  // Phase A2 follow-up #6 — list MY past attempts on one assignment.
  // Summary fields only (no per-Q detail; that would invalidate retake legitimacy).
  myAttempts: (assignmentId: string) => apiClient.get(`/training/my-attempts/${assignmentId}`),

  // Staff — taking an attempt
  startAttempt: (assignmentId: string) =>
    apiClient.post('/training/attempts', { assignment_id: assignmentId }),
  // Resume support — autosave the in-progress answer selections (owner-only).
  saveDraft: (attemptId: string, answers: Record<string, number>) =>
    apiClient.patch(`/training/attempts/${attemptId}/draft`, { answers }),
  enterSlide: (attemptId: string, slideNumber: number) =>
    apiClient.post(`/training/attempts/${attemptId}/slides/${slideNumber}/enter`),
  exitSlide: (attemptId: string, slideNumber: number) =>
    apiClient.patch(`/training/attempts/${attemptId}/slides/${slideNumber}/exit`),
  submitAttempt: (
    attemptId: string,
    answers: Array<{ question_id: string; answer_index: number; time_spent_seconds?: number }>,
  ) => apiClient.post(`/training/attempts/${attemptId}/submit`, { answers }),

  // Admin — completions + audit
  revokeCompletion: (completionId: string, reason: string) =>
    apiClient.post(`/training/completions/${completionId}/revoke`, { reason }),
  audit: (userId: string) => apiClient.get(`/training/audit/${userId}`),
  auditDetail: (userId: string, moduleId: string) =>
    apiClient.get(`/training/audit/${userId}/${moduleId}`),

  // Compliance (Phase 3 unified surface — already wired into the Training Status tab)
  compliance: () => apiClient.get('/training/compliance'),
  complianceExport: () => apiClient.get('/training/compliance/export', { responseType: 'blob' }),
};

// ─── EXAM BUILDER (cross-module certification exams) ──────────────────────────
// Platform roles build exams; practice admins assign + view; staff take.
export const examApi = {
  // Build (platform)
  list: () => apiClient.get('/exams'),
  get: (id: string) => apiClient.get(`/exams/${id}`),
  create: (data: Record<string, unknown>) => apiClient.post('/exams', data),
  update: (id: string, data: Record<string, unknown>) => apiClient.put(`/exams/${id}`, data),
  toggle: (id: string) => apiClient.patch(`/exams/${id}/toggle`),
  // Assign (practice admin)
  assignments: () => apiClient.get('/exams/assignments/list'),
  createAssignment: (data: {
    exam_id: string; assigned_to_user_id?: string; assigned_to_role?: string;
    due_date?: string | null; recurrence?: 'annual' | 'one_time'; auto_renew?: boolean; notes?: string;
  }) => apiClient.post('/exams/assignments', data),
  // Platform-scoped: assign an exam to a RekhaTech platform_user (practice_id NULL, no task).
  createPlatformAssignment: (data: {
    exam_id: string; assigned_to_user_id: string;
    due_date?: string | null; recurrence?: 'annual' | 'one_time'; auto_renew?: boolean;
  }) => apiClient.post('/exams/assignments/platform', data),
  // Bulk platform-scoped: assign one exam to MANY platform users at once. Returns
  // { assigned[], skipped[], assigned_count, skipped_count } — dups + non-platform
  // targets are skipped, not errored. Powers the Certification Exams "Assign" modal.
  createPlatformAssignmentBulk: (data: {
    exam_id: string; assigned_to_user_ids: string[];
    due_date?: string | null; recurrence?: 'annual' | 'one_time'; auto_renew?: boolean;
  }) => apiClient.post('/exams/assignments/platform/bulk', data),
  updateAssignment: (id: string, data: Record<string, unknown>) => apiClient.patch(`/exams/assignments/${id}`, data),
  resetAssignment: (id: string) => apiClient.post(`/exams/assignments/${id}/reset`),
  // Staff — the twin of trainingApi.renewAssignment (same contract, same two
  // success arms); refusals are EXAM_ASSIGNMENT_NOT_FOUND / EXAM_UNAVAILABLE /
  // NOT_EXPIRED / NOT_CERTIFIED, and the successor is
  // `data.assignment.exam_assignment_id`.
  renewAssignment: (id: string) => apiClient.post(`/exams/assignments/${id}/renew`),
  // Staff
  myAssignments: () => apiClient.get('/exams/my-assignments'),
  myCompletions: () => apiClient.get('/exams/my-completions'),
  myAttempts: (examAssignmentId: string) => apiClient.get(`/exams/my-attempts/${examAssignmentId}`),
  startAttempt: (examAssignmentId: string) =>
    apiClient.post('/exams/attempts', { exam_assignment_id: examAssignmentId }),
  // Resume support — autosave the in-progress answer selections (owner-only).
  saveDraft: (attemptId: string, answers: Record<string, number>) =>
    apiClient.patch(`/exams/attempts/${attemptId}/draft`, { answers }),
  enterSlide: (attemptId: string, moduleId: string, slideNumber: number) =>
    apiClient.post(`/exams/attempts/${attemptId}/slides/enter`, { module_id: moduleId, slide_number: slideNumber }),
  exitSlide: (attemptId: string, moduleId: string, slideNumber: number) =>
    apiClient.patch(`/exams/attempts/${attemptId}/slides/exit`, { module_id: moduleId, slide_number: slideNumber }),
  submitAttempt: (
    attemptId: string,
    answers: Array<{ module_id: string; question_id: string; answer_index: number }>,
  ) => apiClient.post(`/exams/attempts/${attemptId}/submit`, { answers }),
  // Admin — completions + compliance
  revokeCompletion: (id: string, reason: string) => apiClient.post(`/exams/completions/${id}/revoke`, { reason }),
  compliance: () => apiClient.get('/exams/compliance'),
  auditDetail: (userId: string, examId: string) => apiClient.get(`/exams/audit/${userId}/${examId}`),
  // Download the holder's exam certificate as a PDF blob (owner-or-admin only).
  downloadCertificate: (completionId: string) =>
    apiClient.get(`/exams/completions/${completionId}/certificate`, { responseType: 'blob' }),
};

// Fire-and-forget draft flush for tab close / navigation (pagehide). Axios can't
// run during unload, and navigator.sendBeacon can't set the Authorization header —
// fetch with keepalive:true is the modern path that does both. Mirrors the
// apiClient request interceptor's auth headers (Bearer + X-Practice-ID). Best
// effort by design: errors are swallowed (the debounced axios autosave is the
// primary persistence; this just narrows the close-the-tab race).
export const flushDraftKeepalive = (
  kind: 'training' | 'exams',
  attemptId: string,
  answers: Record<string, number>,
): void => {
  try {
    if (!attemptId || Object.keys(answers).length === 0) return;
    const { token, currentPractice } = useAuthStore.getState();
    if (!token) return;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    if (currentPractice) headers['X-Practice-ID'] = currentPractice.practice_id;
    const deviceToken = getDeviceToken();
    if (deviceToken) headers['X-Device-Token'] = deviceToken;
    void fetch(`${API_URL}/api/${kind}/attempts/${attemptId}/draft`, {
      method: 'PATCH',
      keepalive: true,
      headers,
      body: JSON.stringify({ answers }),
    }).catch(() => undefined);
  } catch { /* never break unload */ }
};

// ─── UNIFIED CERTIFICATION STATUS (modules + exams, By-person / By-course) ────
// One endpoint, two scopes. scope='platform' forces the RekhaTech staff roster
// (NULL-practice assignments); scope='practice' (default) uses the selected
// practice. Powers the scalable CertificationStatusView (rows-not-columns).
export const certStatusApi = {
  get: (scope: 'platform' | 'practice' = 'practice') =>
    apiClient.get('/cert-status', { params: { scope } }),
};

// ─── TRAINING WIRE ADDITIONS (one declaration, many surfaces) ─────────────────
// Every training/exam/course response type is declared LOCALLY in the surface
// that renders it (MyTrainingPage, CourseJourney, CertificationStatusView,
// CompliancePage) — there is no shared response model to extend. These four
// interfaces are the exception: they are fields the SAME backend change added to
// several payloads at once, so they are declared once here and mixed into the
// local interfaces (`interface MyAssignment extends AttemptTruth { … }`) rather
// than re-typed per surface and allowed to drift.
//
// All four are OPTIONAL: frontend and backend deploy in separate steps, so every
// reader must still behave correctly against a backend that predates them.

/** Attempt truth — GET /training/my-assignments · GET /exams/my-assignments ·
 *  each chapter of GET /training-tracks/my/:enrollmentId/journey.
 *
 *  Why both: the pre-existing counts (`current_attempt_number` on the two
 *  my-assignments payloads, `attempts_used` on a journey chapter) are raw
 *  COUNT(*)s that INCLUDE an attempt still open, and they keep that meaning.
 *  Exhaustion must be derived from `submitted_attempts`; an open attempt is a
 *  RESUME, not a retry — POST /training/attempts and POST /exams/attempts return
 *  the open attempt BEFORE they reach their max-attempts and retry-wait guards,
 *  so a Continue affordance for `has_open_attempt` is never cooldown-blocked. */
export interface AttemptTruth {
  /** Attempts with submitted_at IS NOT NULL. 0 when none. */
  submitted_attempts?: number;
  /** An attempt row exists with submitted_at IS NULL (resumable). */
  has_open_attempt?: boolean;
}

/** GET /exams/my-attempts/:examAssignmentId — the two OVERALL bars the grader
 *  applies alongside the per-section gates in `module_breakdown` (it passes only
 *  when all three hold). NULL = that bar is not configured on the exam.
 *
 *  These are the exam's CURRENT settings, not a snapshot at attempt time (the
 *  attempt row stores `exam_version`, not the bars), so state them as the bar
 *  that applies — never as what a historical attempt was graded against.
 *  `module_breakdown`'s per-section `min_correct` IS snapshotted at grading.
 *  The sibling module endpoint (/training/my-attempts/:id) does not carry these:
 *  a module has no total-correct gate, and no section breakdown to explain. */
export interface PastAttemptBars {
  pass_score_percent?: number | null;
  min_correct_total?: number | null;
}

/** GET /cert-status → `courses[]`. The catalog now also returns DEACTIVATED
 *  modules/exams (an item can reference one — the item queries filter on the
 *  ASSIGNMENT's is_active, not the target's), so a name is always available
 *  instead of a raw UUID. Render a false value as withdrawn, don't hide it. */
export interface CertStatusCatalogFlags {
  is_active?: boolean;
}

/** GET /training/compliance → each staff × module row. Course provenance for the
 *  cell's assignment (mig 215): non-null when a course enrollment materialized
 *  it. Both are null for manual/adopted assignments and for empty cells. */
export interface TrainingComplianceProvenance {
  source_enrollment_id?: string | null;
  track_name?: string | null;
}

// ─── TRAINING COURSES (wire/code name: TRACK) ────────────────────────────────
// A course = a platform-authored ORDERED composition of chapters (training
// modules + an optional final exam). One enrollment; the platform enforces
// strictly-linear progression — pass chapter N and chapter N+1 opens at once.
// UI strings say "course" everywhere; the code says track ("course" is already
// taken by cert-status as the modules∪exams union).
export const trackApi = {
  // Authoring (platform_admin) + the roster read (compliance roles).
  list: () => apiClient.get('/training-tracks'),
  get: (id: string) => apiClient.get(`/training-tracks/${id}`),
  create: (data: Record<string, unknown>) => apiClient.post('/training-tracks', data),
  update: (id: string, data: Record<string, unknown>) => apiClient.put(`/training-tracks/${id}`, data),
  toggle: (id: string) => apiClient.patch(`/training-tracks/${id}/toggle`),

  // Enrollment. ONE click = enroll + credit every already-passed chapter +
  // materialize the first unpassed one. No due date on an enrollment (cut).
  enroll: (data: { track_id: string; assigned_to_user_id: string; notes?: string }) =>
    apiClient.post('/training-tracks/enrollments', data),
  enrollPlatformBulk: (data: { track_id: string; assigned_to_user_ids: string[] }) =>
    apiClient.post('/training-tracks/enrollments/bulk', data),
  // (No roster read here on purpose: GET /training-tracks/enrollments is scoped by
  // the X-Practice-ID header, so on a PLATFORM-scoped surface with a practice
  // selected it answers about the wrong tenant. Both surfaces that used it now
  // read enrollments off GET /cert-status, which scopes the roster and the
  // enrollments in the SAME request.)
  // Un-enroll = cancel. Degrade-to-manual: the chapters already materialized keep
  // working as plain assignments, and completions are never touched. The
  // justification key is `reason` — the server schema declares `reason` and
  // validate() strips unknown keys, so any other name lands in the audit as null.
  unenroll: (enrollmentId: string, reason?: string) =>
    apiClient.patch(`/training-tracks/enrollments/${enrollmentId}`, { status: 'cancelled', ...(reason ? { reason } : {}) }),
  // Waive the chapter the learner is ON (platform_admin). There is no target
  // position: a waive excuses the CURRENT chapter and the cursor moves to the
  // next one — waiving the last chapter completes the course. The waived chapter
  // renders as "Waived", never as passed. (Route path stays /skip.)
  //
  // `expected_position` is NOT a target — it is the position the admin's screen
  // said was current when they wrote the reason. The server refuses (409
  // CURSOR_MOVED / CHAPTER_ALREADY_PASSED) rather than applying a justification
  // written about one chapter to whichever chapter the cursor has since reached.
  waiveCurrentChapter: (enrollmentId: string, data: { reason: string; expected_position: number }) =>
    apiClient.post(`/training-tracks/enrollments/${enrollmentId}/skip`, data),
  // Idempotent self-heal: advances the cursor over anything already passed and
  // materializes the current chapter. The journey UI calls this on mount, then
  // (re)fetches the journey — the journey GET itself stays a pure read.
  ensure: (enrollmentId: string) => apiClient.post(`/training-tracks/enrollments/${enrollmentId}/ensure`),

  // Learner.
  myCourses: () => apiClient.get('/training-tracks/my'),
  journey: (enrollmentId: string) => apiClient.get(`/training-tracks/my/${enrollmentId}/journey`),
};

// ─── CENTRAL FAX ──────────────────────────────────────────────────────────────
// Inbox/queue/repository + triage + admin config (taxonomy, purpose owners,
// rules, thresholds, SLA, RingCentral connection) + analytics.
export const faxApi = {
  // inbox / repository / queue
  list: (params?: Record<string, unknown>) => apiClient.get('/faxes', { params }),
  // F10.5 (fax-as-channel): the fax parallel work-queue (queue / work / team-queue /
  // exceptions / claim / release) is RETIRED. Accountable work + the four work views
  // now live on the TASK surface (tasksApi); the fax inbox is triage + repository only.
  needsReview: () => apiClient.get('/faxes/needs-review'),
  get: (id: string) => apiClient.get(`/faxes/${id}`),
  viewUrl: (id: string) => apiClient.get(`/faxes/${id}/view`),
  downloadUrl: (id: string) => apiClient.get(`/faxes/${id}/download`),
  audit: (id: string) => apiClient.get(`/faxes/${id}/audit`),
  analytics: (params?: Record<string, unknown>) => apiClient.get('/faxes/analytics', { params }),
  // fax.7 — ops insights: aging & SLA (7.3) + runtime coverage telemetry (7.4).
  aging: () => apiClient.get('/faxes/aging'),
  coverageTelemetry: () => apiClient.get('/faxes/coverage-telemetry'),
  // fax.4.3 — human-confirmed fan-in (proposals: the waiting tasks a fax may resolve)
  proposals: (id: string) => apiClient.get(`/faxes/${id}/proposals`),
  proposalsForTask: (taskId: string) => apiClient.get(`/faxes/proposals/by-task/${taskId}`),
  confirmProposal: (proposalId: string) => apiClient.post(`/faxes/proposals/${proposalId}/confirm`),
  rejectProposal: (proposalId: string) => apiClient.post(`/faxes/proposals/${proposalId}/reject`),
  undoProposal: (proposalId: string) => apiClient.post(`/faxes/proposals/${proposalId}/undo`),
  // triage
  // `syncTasks` (3-feature batch): when the MODULE changes, re-route the fax's OPEN
  // linked task(s) to the new module's queue (assignee kept; completed tasks untouched).
  // `reason` (fax-inbox reason door) — REQUIRED by the server (400 REASON_REQUIRED) only when a
  // sync'd reclassify moves a linked OPEN records_release case out of the module; sent when present.
  reclassify: (id: string, categoryId: string, facets?: Record<string, unknown>, syncTasks?: boolean, reason?: string) =>
    apiClient.post(`/faxes/${id}/reclassify`, {
      category_id: categoryId,
      ...(facets || {}),
      ...(syncTasks ? { sync_tasks: true } : {}),
      ...(reason && reason.trim() ? { reason: reason.trim() } : {}),
    }),
  // PHI-free count + titles of the OPEN tasks linked to this fax — drives the
  // "moving the module also moves the linked task" confirm dialog on reclassify.
  linkedOpenTasks: (id: string) =>
    apiClient.get(`/faxes/${id}/linked-open-tasks`) as Promise<{
      // has_records_release — whether ANY open linked task is on the records_release module; the
      // reclassify confirm keys its required-reason gate on this (not the fax's category_key).
      data: { data: { count: number; tasks: Array<{ task_id: string; title: string }>; has_records_release: boolean } };
    }>,
  linkPatient: (id: string, patientId: string | null) => apiClient.post(`/faxes/${id}/link-patient`, { patient_id: patientId }),
  createTask: (id: string, data: Record<string, unknown>) => apiClient.post(`/faxes/${id}/create-task`, data),
  reprocess: (id: string) => apiClient.post(`/faxes/${id}/reprocess`),
  // RingCentral fax recovery — re-pull a STUCK fax (no stored PDF) from RingCentral while
  // it's still within retention (~30 days). 202 = re-queued (refresh shortly); 410 =
  // unrecoverable (RC purged it; marked terminal); 503 = RC unavailable (retry); 400 =
  // ALREADY_STORED (use reprocess). Platform-configurator + practice-scope gated server-side.
  recoverFromRingCentral: (id: string) => apiClient.post(`/faxes/${id}/recover-from-ringcentral`),
  // Stuck-fax visibility — faxes captured with NO stored PDF in a non-terminal status.
  // Returns { count, data } for the inbox "N stuck" indicator + filter.
  stuck: () => apiClient.get('/faxes/stuck'),
  // single-fax triage disposition (new | acknowledged | handled). Bulk triage + the
  // handled-while-open 409 warn were retired in F10.5 (one list — nothing to reconcile).
  triage: (id: string, state: string) => apiClient.post(`/faxes/${id}/triage`, { state }),
  // fax.5.2 — junk verdict: cancel the tasks this fax spawned + mark it handled.
  junk: (id: string, reason?: string) => apiClient.post(`/faxes/${id}/junk`, reason ? { reason } : {}),
  // fax.2 inbound-quality actions
  fileToDocuments: (id: string, patientId?: string | null) =>
    apiClient.post(`/faxes/${id}/file-to-documents`, patientId ? { patient_id: patientId } : {}),
  addNote: (id: string, body: string) => apiClient.post(`/faxes/${id}/notes`, { body }),
  forward: (id: string, toUserId: string, message?: string) =>
    apiClient.post(`/faxes/${id}/forward`, { to_user_id: toUserId, message }),
  // outbound (fax.6) — the server now prefers `document_ids` (ordered, 1..20; merged into
  // ONE PDF in that order, sent as a single transmission) over the legacy single `document_id`
  // (kept for back-compat for other callers). This composer always sends `document_ids`.
  //
  // Cover Pages P1 (R5 handshake): `send` now REQUIRES `preview_id` (from a prior `preview` call)
  // and carries the per-send `cover_template_id` (uuid | 'house'). The server recomputes the same
  // compose-hash from these fields — `document_ids` (order + dedup), `to_number`, `cover_template_id`
  // and `cover_note` MUST byte-match the `preview` call that produced `preview_id`, or the send is
  // refused with 409 { code: 'PREVIEW_REQUIRED' }. `connection_id`/`patient_id` are NOT in the hash.
  send: (payload: {
    to_number: string;
    document_ids?: string[];
    document_id?: string;
    connection_id?: string | null;
    patient_id?: string | null;
    // Chart filing — with a linked patient the sent fax is filed in that patient's chart Documents by
    // DEFAULT (absent ⇒ true). false opts out of the chart copy only; the fax is still logged against
    // the patient in the Sent tab + audit. NOT a compose-hash input.
    save_to_chart?: boolean;
    cover_note?: string | null;
    // Send Fax "memory" batch (mig 200) — the optional operator-stated recipient name/organization
    // (recipient IDENTITY, PHI-free). Persisted on the outbound row; NOT a compose-hash input (an existing
    // preview stays valid). Server trims + caps at 120. Sent by FaxComposer when non-empty.
    recipient_name?: string;
    cover_template_id?: string;   // uuid | 'house'; omit for R2-OFF (server resolves cover='none')
    // Cover Pages v2 — dynamic cover-field values, keyed by field name (text/textarea → string,
    // checkbox → boolean, radio/dropdown → an exact option value). Validated + canonicalized server-
    // side against the resolved APPROVED template's field_specs; MUST match the preview call byte-for-
    // byte (they feed the same compose-hash) or the send is refused 409 PREVIEW_REQUIRED.
    cover_fields?: Record<string, string | boolean>;
    preview_id?: string;          // required by the server (409 PREVIEW_REQUIRED when missing/stale)
    // View-before-send Phase A — the in-app viewer attestation. pages_seen MUST equal the previewed
    // artifact's authoritative page count (server verifies against the scratch metadata; mismatch →
    // 409 { code: 'PAGES_NOT_VIEWED' }). render_fallback: true = the in-app viewer failed and the
    // user attested via the new-tab fallback (audited server-side); omit otherwise.
    pages_seen?: number;
    render_fallback?: boolean;
  }) => apiClient.post('/faxes/send', payload),
  // Cover Pages P1 R5 — the MANDATORY pre-send preview. Builds the exact merged PDF (cover page 1 +
  // docs) to a hash-keyed scratch object WITHOUT dispatching or creating a documents row, and returns
  // a 15-minute presigned INLINE url. Same gate stack as `send`. Pass `prior_preview_id` when
  // re-previewing so the superseded scratch object is deleted. Response:
  // { data: { preview_id, url, page_count, cover_kind: 'template'|'house'|'none', byte_length, hash } }.
  // (View-before-send Phase A: `hash` — the compose hash — keys the same-origin previewView stream
  // below (sent via the x-fax-preview-hash header, never a query param) so the in-app viewer can
  // fetch the exact artifact.)
  // Errors incl. 400 DOC_UNREADABLE (unsupported attachment), 413 MERGED_TOO_LARGE, 502 PREVIEW_FAILED.
  preview: (payload: {
    to_number: string;
    document_ids?: string[];
    document_id?: string;
    cover_template_id?: string;   // uuid | 'house'; omit for R2-OFF
    cover_note?: string | null;
    patient_id?: string | null;
    // Cover Pages v2 — same dynamic cover-field map the send carries; send it IDENTICALLY on preview
    // and send (the server canonicalizes both into the compose-hash). Required-missing / bad-option /
    // too-long / fill failures surface as 400 COVER_FIELD_* / COVER_FILL_FAILED here (never on send).
    cover_fields?: Record<string, string | boolean>;
    prior_preview_id?: string;    // supersede-delete the caller's previous preview object
  }) => apiClient.post('/faxes/preview', payload),
  // View-before-send Phase A — same-origin stream of the previewed artifact (Content-Type:
  // application/pdf, Cache-Control: private, no-store) for the in-app FaxPreviewPane. Same 4-gate
  // stack as preview/send; 409 PREVIEW_REQUIRED-style when the scratch is missing/expired. The
  // compose hash the preview response returned rides the `x-fax-preview-hash` HEADER exclusively —
  // NEVER a query param (req.url lands in request logs, which would leak the live streaming
  // credential). Also views a prepare-resend artifact (the prepared scratch rides the same preview
  // mechanics — pass prepared_id + its hash).
  previewViewBlob: (previewId: string, hash: string) =>
    apiClient.get(`/faxes/preview/${previewId}/view`, { headers: { 'x-fax-preview-hash': hash }, responseType: 'blob' }),
  // F4 — `q` is the Sent-list smart search term (bare number → fax-number match; text → patient-name
  // search). The page only sends it when non-empty (classifySentSearch); the server owns the routing.
  outbound: (params?: { limit?: number; offset?: number; status?: string; date_from?: string; date_to?: string; q?: string }) =>
    apiClient.get('/faxes/outbound', { params }),
  // Send Fax "memory" batch (mig 200) — the composer's recents / directory picker: up to 8 recently-faxed
  // recipient numbers for the selected practice (most-recent first), each with its most-recent recipient
  // name + a usage count. Same practice-scoped gate as `outbound` (FAX_PRACTICE_REQUIRED without a selected
  // practice). Response: { data: Array<{ to_number, recipient_name, last_used_at, times_used }> }. PHI-free
  // (recipient identity + numbers only). Consumed by batch 5b's recents dropdown.
  recipients: () => apiClient.get('/faxes/recipients'),
  // Open the transmitted document of a Sent fax inline (short-lived signed URL). The endpoint
  // enforces the per-document minimum-necessary ACL + audits the view. BE returns the URL at the
  // body root ({ url, page_count }, mirroring GET /faxes/:id/view) — the caller reads resp.data.url.
  // Only meaningful for rows that carry a document_id. CAUTION: page_count here is PROVIDER-
  // reported (null on many failed rows / can include a provider cover under R1) — display only;
  // it must never feed the view gate or the pages_seen attestation (the server validates those
  // against the stored artifact's own bytes).
  viewOutbound: (id: string) => apiClient.get(`/faxes/outbound/${id}/view`),
  // View-before-send Phase A — same-origin byte stream of the transmitted artifact (IDENTICAL ACL
  // chain to the JSON /view route incl. the per-document ACL + audit; §8.6 no-store headers). Feeds
  // the resend-review viewer for covered rows (cover_kind NOT NULL — a frozen artifact exists).
  // Returns { blob, pageCount? }: pageCount is parsed from the X-Page-Count response header — the
  // SERVER-VALIDATED count (the same countStoredPdfPages number the resend confirm validates
  // pages_seen against; PHI-free integer), so it MAY back the §8.9 fallback attestation for
  // covered rows. Header absent/invalid (older backend) ⇒ pageCount undefined — degrade to
  // render-only arming.
  viewOutboundStream: async (id: string): Promise<{ blob: Blob; pageCount?: number }> => {
    const r = await apiClient.get(`/faxes/outbound/${id}/view/stream`, { responseType: 'blob' });
    const n = Number((r.headers as Record<string, unknown>)?.['x-page-count']);
    return { blob: r.data as Blob, pageCount: Number.isInteger(n) && n >= 1 ? n : undefined };
  },
  refreshOutbound: (id: string) => apiClient.post(`/faxes/outbound/${id}/refresh`),
  // View-before-send Phase A — legacy rows (cover_kind NULL: the house-wrap used to happen AT
  // resend) prepare first: the server merges the wrapped bytes into the preview scratch and returns
  // { data: { prepared_id, hash, page_count } }; view it via previewViewBlob, then pass prepared_id
  // on the resend so the server transmits the EXACT previewed scratch (byte-parity rule).
  prepareResend: (id: string) => apiClient.post(`/faxes/outbound/${id}/prepare-resend`),
  // View-before-send Phase A — resend now carries the view attestation + an idempotency key:
  // pages_seen must equal the artifact's page count (409 PAGES_NOT_VIEWED otherwise); dispatch_key
  // is a client nonce (crypto.randomUUID) so a retry of the SAME confirm can never double-transmit —
  // the server returns a GENUINELY-dispatched row (sent/sending/delivered) flagged
  // { duplicate: true } inside data (treat as success); a concurrent duplicate confirm while the
  // winner is still mid-dispatch gets 409 SEND_IN_PROGRESS instead (NOT a success — static copy,
  // user checks the Sent tab).
  // prepared_id only for legacy rows (from prepareResend); a legacy confirm whose prepared scratch
  // is missing/expired → 409 PREPARE_REQUIRED (re-run prepareResend, re-view, confirm again).
  // render_fallback only on the new-tab fallback path.
  resendOutbound: (id: string, body: {
    pages_seen: number;
    dispatch_key: string;
    prepared_id?: string;
    render_fallback?: boolean;
    connection_id?: string | null;
    // F2 — the (optionally corrected) destination fax number. Always sent by the overlay; an unedited
    // original passes validation = today's same-number behavior. Founder rule (2026-07-19) — "Resend
    // everywhere": the number is editable on EVERY terminal row (failed/unconfirmed/sent); a delivered-row
    // number change is accepted server-side (guarded by cover regeneration + the fax_destination_corrected
    // audit, not a block).
    to_number?: string;
  }) => apiClient.post(`/faxes/outbound/${id}/resend`, body),
  // The composer's "Send from" list. Unlike `connections()` (admin-only config list,
  // requirePracticeAdmin), this is gated IDENTICALLY to `send` (canActOnTasks + the
  // selected-practice scope), so an authorized non-admin sender gets a 200 — not a 403 —
  // and the rows are the credentials-free, already-send-capable lines for the SELECTED
  // practice (the exact set the send resolver would pick; no client-side filtering).
  sendLines: () => apiClient.get('/faxes/send-lines'),
  // Send M polish — upload an ad-hoc attachment to fax out; patient OPTIONAL. Multipart
  // (field 'file' + optional 'patient_id') → { data: { document_id, file_name, mime_type } }.
  // Accepts PDF/JPEG/PNG ≤25MB (400 on bad type/size); gated like send (403
  // FAX_SEND_NOT_DESIGNATED when the practice restricts and the caller isn't designated).
  uploadOutbound: (file: File, patientId?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    if (patientId) fd.append('patient_id', patientId);
    return apiClient.post('/faxes/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // fax.3.1 — coverage gaps (admin "needs setup" panel).
  coverageGaps: () => apiClient.get('/faxes/config/coverage-gaps'),
  // config — taxonomy
  taxonomy: (includeInactive = false) =>
    apiClient.get('/faxes/config/taxonomy', { params: includeInactive ? { include_inactive: 1 } : {} }),
  updateCategory: (id: string, data: Record<string, unknown>) => apiClient.patch(`/faxes/config/taxonomy/${id}`, data),
  // config — sub-categories (G3: per-module intake classification catalog)
  subcategories: (moduleId?: string) =>
    apiClient.get('/faxes/config/subcategories', { params: moduleId ? { module_id: moduleId } : {} }),
  createSubcategory: (data: Record<string, unknown>) => apiClient.post('/faxes/config/subcategories', data),
  updateSubcategory: (id: string, data: Record<string, unknown>) => apiClient.patch(`/faxes/config/subcategories/${id}`, data),
  deleteSubcategory: (id: string) => apiClient.delete(`/faxes/config/subcategories/${id}`),
  // config — eligible users picker (assignee / forward recipient).
  // G9 Stage 3c: the fax_purpose_owners routes (purposeOwners / setPurposeOwners) are
  // RETIRED — per-member view/claim scope moved to PATCH /queues/.../scope. This
  // endpoint is KEPT: it lists eligible practice staff + assigned platform actors and
  // backs the Create-task assignee + Forward recipient pickers.
  eligibleOwners: () => apiClient.get('/faxes/config/eligible-owners'),
  // config — rules
  rules: () => apiClient.get('/faxes/config/rules'),
  createRule: (data: Record<string, unknown>) => apiClient.post('/faxes/config/rules', data),
  updateRule: (id: string, data: Record<string, unknown>) => apiClient.patch(`/faxes/config/rules/${id}`, data),
  deleteRule: (id: string) => apiClient.delete(`/faxes/config/rules/${id}`),
  // config — thresholds + sla
  thresholds: () => apiClient.get('/faxes/config/thresholds'),
  updateThresholds: (data: Record<string, unknown>) => apiClient.put('/faxes/config/thresholds', data),
  sla: () => apiClient.get('/faxes/config/sla'),
  updateSla: (data: Record<string, unknown>) => apiClient.put('/faxes/config/sla', data),
  aiModels: () => apiClient.get('/faxes/config/ai-models'),
  aiModel: () => apiClient.get('/faxes/config/ai-model'),
  updateAiModel: (model_id: string | null) => apiClient.put('/faxes/config/ai-model', { model_id }),
  // config — fax provider connections (multi-provider, multi-account)
  connection: () => apiClient.get('/faxes/config/connection'),            // singular primary view — back-compat
  connections: () => apiClient.get('/faxes/config/connections'),          // plural list (manage-connections UI)
  connect: (payload: {
    provider?: string; server_url?: string; webhook_secret?: string;
    client_id?: string; client_secret?: string; jwt?: string;            // RingCentral
    app_id?: string; api_key?: string; user_id?: string;                 // eFax-REST
    account_id?: string; username?: string; password?: string;           // eFax-XML
  }) => apiClient.post('/faxes/config/connection', payload),
  disconnect: () => apiClient.delete('/faxes/config/connection'),         // practice-wide — back-compat
  disconnectConnection: (connectionId: string) => apiClient.delete(`/faxes/config/connections/${connectionId}`),
  setPrimaryConnection: (connectionId: string) => apiClient.patch(`/faxes/config/connections/${connectionId}/primary`),
  // config — correspondents / sender directory (fax.2.2)
  correspondents: () => apiClient.get('/faxes/config/correspondents'),
  createCorrespondent: (data: Record<string, unknown>) => apiClient.post('/faxes/config/correspondents', data),
  updateCorrespondent: (id: string, data: Record<string, unknown>) => apiClient.patch(`/faxes/config/correspondents/${id}`, data),
  addCorrespondentAlias: (id: string, faxNumber: string, label?: string) =>
    apiClient.post(`/faxes/config/correspondents/${id}/aliases`, { fax_number: faxNumber, label }),
  removeCorrespondentAlias: (aliasId: string) => apiClient.delete(`/faxes/config/correspondents/aliases/${aliasId}`),
};

// ─── Cover Pages P1 (REQUIREMENTS v2) ───────────────────────────────────────────
// The composer's cover picker rides GET /faxes/send-lines (no extra round trip): its response
// gains a `cover` block. R2 OFF ⇒ the FE hides the picker; `templates` is approved-only.
// Cover Pages v2 — one operator-fillable field detected on a cover template at upload preflight. Mirrors
// the backend FieldSpec (coverPageService.ts): `name`/`kind`/`options`/`maxLength`/`required` come from
// the artwork and are IMMUTABLE; `label` (all kinds) and `optionLabels` (RADIO only — its export values
// are unreachable artwork text) are curatable in the admin Fields editor. `defaultValue` is the artwork's
// own default (text default / checkbox checked state / radio-dropdown selected option). `order` is the
// visual order (page y-desc, x-asc). Dropdown options print their own option text, so they have no
// optionLabels. NULL field_specs on a template row = a pre-feature template (no operator fields).
export interface FieldSpec {
  name: string;
  kind: 'text' | 'textarea' | 'checkbox' | 'radio' | 'dropdown';
  label: string;
  options?: string[];                          // radio/dropdown export values (immutable)
  optionLabels?: { [value: string]: string };  // radio only — display labels (curatable)
  maxLength?: number;                          // text/textarea only (the PDF field maxLength)
  required?: boolean;
  defaultValue?: string | boolean;
  // Cover-engine batch (additive; absent on pre-batch templates). `section` = an admin-curated grouping
  // header (the composer emits a section header when it changes across specs). `inputHint: 'date'` (text
  // kind only) = the composer renders a date picker whose stored value stays a capped MM/DD/YYYY string.
  // Both are curated in the admin Fields editor; buildFieldSpecs does NOT read them from the artwork.
  section?: string;
  inputHint?: 'date';
  // ai.326 phase 3 — admin-curated (Fields editor; ai.333: a draft or an approved version): may the AI suggest a value for this TEXT field, and its format.
  aiAllowed?: boolean;
  aiFormat?: 'date' | 'cpt' | 'hcpcs' | 'icd10' | 'name' | 'text';
  // ai.337 — admin-curated "Hint for the AI" (a tick box, or a text field while aiAllowed; never a pick-list or a
  // locked name), at most 500 characters. Editable on a draft or an approved version. The admin list carries it;
  // the composer's send-lines payload never does (stripped server-side).
  aiHint?: string;
  order: number;
}

export interface SendLinesCoverTemplate {
  template_id: string;
  name: string;
  version: number;
  is_default: boolean;
  // Cover Pages v2 — the approved version's operator-field inventory, in visual order. Absent/NULL ⇒ no
  // dynamic fields (pre-feature template or a template with none) ⇒ the composer renders exactly the P1 UI.
  field_specs?: FieldSpec[] | null;
}
export interface SendLinesCover {
  rcmtask_cover: boolean;               // R2 — false ⇒ hide the picker (cover = none)
  provider_cover: boolean;              // R1 — the fax provider's own cover page
  default_template_id: string | null;   // the practice default (pre-selected in the picker)
  templates: SendLinesCoverTemplate[];   // approved templates only
}

// One row of the practice's cover-template library (GET /fax-cover-templates). One row = one
// immutable template VERSION; versions of one logical template share `template_group_id`.
export interface CoverTemplateRow {
  template_id: string;
  template_group_id: string;
  version: number;
  name: string;
  status: 'draft' | 'approved' | 'retired';
  is_default: boolean;
  acro_fields: string[];               // std/known field names found at preflight
  // Cover Pages v2 — the detected operator-field inventory (curatable labels before approve). ai.333 —
  // on approve the labels, sections, date inputs and option labels lock; the per-field AI switch and
  // format stay changeable on an approved version (each change is a history row). NULL = pre-feature
  // template. The admin Fields editor reads/writes this via updateFieldSpecs.
  field_specs?: FieldSpec[] | null;
  preflight_warnings: unknown[];        // non-fatal preflight notes (PHI-free)
  attested_by?: string | null;
  attested_at?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  retired_at?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  // ai.326 B4 — this form's own AI prompt (an ai_prompt_groups id; null = no AI prompt: Ask AI is disabled
  // on this cover page, D-2026-10-07-04).
  ai_prompt_group_id?: string | null;
}

// ai.333 — one line of a cover template version's AI settings history (GET
// /fax-cover-templates/:id/ai-settings-history), newest first. `setting` + `field_name` are parsed from
// the audit row's field_changed (`field_name` null for ai_prompt). old/new are closed tokens: on/off
// (ai_allowed), a format or 'auto' (ai_format), a prompt group id or 'none' (ai_prompt), none/set (ai_hint,
// ai.337: never the hint text). The server sends no labels; the screen maps field_name to the version's own
// (locked) field_specs label.
export interface CoverAiSettingsHistoryEntry {
  seq: string | number;
  at: string;
  actor_name: string | null;
  setting: 'ai_allowed' | 'ai_format' | 'ai_prompt' | 'ai_hint' | string;
  field_name: string | null;
  old: string | null;
  new: string | null;
  // ai_prompt rows only: the prompt NAMES snapshotted when the change was made (never prompt text).
  prompt_names?: { old?: string | null; new?: string | null } | null;
}
export interface CoverAiSettingsHistoryPage {
  entries: CoverAiSettingsHistoryEntry[];
  next_before: string | null;
}

// ai.326 B2 — what the server carried from the latest earlier version on a new-version upload
// (POST /fax-cover-templates 201 `data.carried_forward`; null for a brand-new template).
export interface CoverCarriedForward {
  from_version: number;
  fields_carried: number;
  labels_compared: boolean;
  skipped: 'too_large' | null;
  // ai.337 (Rev 3 R4) — how many carried hints for the AI sit on a field whose own PDF tooltip changed (a
  // count, never text; 0 when labels_compared is false). Absent from a pre-ai.337 server.
  hints_tooltip_changed?: number;
  // ai.337 (N11) — 'too_large' when the hints were left out so the rest of the curation could carry.
  hints_skipped?: 'too_large' | null;
}

// P16 — the additive R2 context sibling key on GET /fax-cover-templates. The library-only cohort
// (rank≥Manager) can't reach the R1/R2 toggles, so the list carries whether the RCMTask cover is ON
// for this practice: when false, the whole library is inert (approved templates aren't used on sends).
// Coerced server-side with the documented inverted default (absent ⇒ true). Booleans only, PHI-free.
export interface CoverLibraryContext {
  rcmtask_cover: boolean;
}

// The cover-template library admin family (mounted at /fax-cover-templates; the server gate,
// requireCoverTemplateManage, keys off the SELECTED practice from the request context — so there
// is no practiceId in these paths). Consumed by the Admin → Central Fax → Cover Pages surface.
export const coverTemplatesApi = {
  // Library list (all statuses; the composer's picker uses the send-lines `templates` instead).
  list: () => apiClient.get('/fax-cover-templates'),
  // Upload a new template (or a new VERSION of one via `templateGroupId`). PDF only, 5MB cap.
  // `attestation` is the required R3 dual attestation checkbox (server 400 ATTESTATION_REQUIRED
  // unless it arrives as 'true'). Preflight hard-fails surface as 400 with a COVER_* code.
  upload: (file: File, name: string, opts?: { templateGroupId?: string; attestation?: boolean }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', name);
    if (opts?.templateGroupId) fd.append('template_group_id', opts.templateGroupId);
    fd.append('attestation', opts?.attestation ? 'true' : 'false');
    return apiClient.post('/fax-cover-templates', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Sample-filled preview PDF (fixed non-PHI values), streamed inline + never stored. Fetched as a
  // blob → object URL for the popup-safe viewer tab.
  samplePreview: (id: string) => apiClient.get(`/fax-cover-templates/${id}/preview`, { responseType: 'blob' }),
  approve: (id: string) => apiClient.post(`/fax-cover-templates/${id}/approve`),
  retire: (id: string) => apiClient.post(`/fax-cover-templates/${id}/retire`),
  // Set (uuid) or clear (null → falls back to HOUSE) the practice default.
  setDefault: (templateId: string | null) => apiClient.put('/fax-cover-templates/default', { template_id: templateId }),
  deleteDraft: (id: string) => apiClient.delete(`/fax-cover-templates/${id}`),
  // Cover Pages v2 — curate a DRAFT template's operator-field labels (all kinds) + radio per-option
  // display labels. Only `label`, `option_labels` (radio only), and the cover-engine additions
  // `section` (all kinds) / `inputHint` ('date', text only) mutate; name/kind/options/flags are
  // immutable (ignored server-side). A draft takes every key. ai.333 — an APPROVED version takes ONLY
  // { name, ai_allowed, ai_format, ai_hint } per edit (any other key → 409 NOT_A_DRAFT; each changed setting
  // is one history row); a RETIRED version → 409 TEMPLATE_RETIRED. ai.337 — `ai_hint` (text, or null to
  // remove) is sent only for a field whose hint changed.
  // → { data: { template_id, field_specs[, changed] } }. 400 COVER_FIELD_UNKNOWN / COVER_FIELD_OPTION /
  // BAD_FIELDS / COVER_FIELD_AI_LOCKED.
  updateFieldSpecs: (
    id: string,
    fields: Array<{
      name: string; label?: string; option_labels?: Record<string, string>; section?: string; input_hint?: 'date' | null;
      ai_allowed?: boolean | null; ai_format?: 'date' | 'cpt' | 'hcpcs' | 'icd10' | 'name' | 'text' | null;
      ai_hint?: string | null;
    }>,
  ) => apiClient.patch(`/fax-cover-templates/${id}/field-specs`, { fields }),
  // ai.326 B4 — the cover prompts this practice may link to a form:
  // { data: [{ prompt_group_id, name, model_label }] } (names, ids and the model label; never text or version).
  // A prompt the practice cannot run is left out.
  listAiPrompts: () => apiClient.get('/fax-cover-templates/ai-prompts'),
  // ai.326 B4 — link (uuid) or clear (null) a form's own AI prompt. ai.333 — a draft or an APPROVED
  // version (an approved change is one history row); 409 TEMPLATE_RETIRED on a retired version;
  // 400 AI_PROMPT_NOT_AVAILABLE / BAD_AI_PROMPT.
  setAiPrompt: (id: string, promptGroupId: string | null) =>
    apiClient.put(`/fax-cover-templates/${id}/ai-prompt`, { prompt_group_id: promptGroupId }),
  // ai.333 — a version's AI settings history, newest first, `before` = the previous page's next_before.
  // → { data: CoverAiSettingsHistoryPage }. Same gate as the rest of this family.
  aiSettingsHistory: (id: string, before?: string | null) =>
    apiClient.get(`/fax-cover-templates/${id}/ai-settings-history`, { params: before ? { before } : {} }),
};

// ─── Correspondent (sender) trust levels — weights the patient match at ingest ─
export const FAX_TRUST_LEVELS = [
  { value: 'trusted', label: 'Trusted', badge: 'green' },
  { value: 'normal', label: 'Normal', badge: 'gray' },
  { value: 'untrusted', label: 'Untrusted', badge: 'red' },
] as const;

export const FAX_CORRESPONDENT_KINDS = [
  { value: 'provider', label: 'Referring provider' },
  { value: 'payer', label: 'Payer' },
  { value: 'facility', label: 'Facility' },
  { value: 'pharmacy', label: 'Pharmacy' },
  { value: 'other', label: 'Other' },
] as const;

// ─── Fax facets (orthogonal to category) ────────────────────────────────────────
// service_type (sub-category) is fully data-driven now: the module_subcategories
// catalog, fetched via faxApi.subcategories() (query key ['fax-subcategories']).
// The former FAX_SERVICE_TYPES static fallback was retired in Phase 3 — every
// consumer reads the live catalog and resolves labels from it.

export const FAX_DECISION_STATUSES = [
  { value: 'request', label: 'Request', badge: 'gray' },
  { value: 'approved', label: 'Approved', badge: 'green' },
  { value: 'partially_approved', label: 'Partial', badge: 'amber' },
  { value: 'denied', label: 'Denied', badge: 'red' },
  { value: 'pended', label: 'Pended', badge: 'amber' },
  { value: 'info_requested', label: 'Info requested', badge: 'amber' },
] as const;

// ─── Fax triage disposition (HUMAN worklist state, set manually) ──────────────
// Orthogonal to the automatic signals (needs_review / is_duplicate / has_task):
// this tracks whether a person has dealt with the fax. `badge` keys Badge colors.
export const FAX_TRIAGE_STATES = [
  { value: 'new', label: 'New', badge: 'gray' },
  { value: 'acknowledged', label: 'Acknowledged', badge: 'blue' },
  { value: 'handled', label: 'Handled', badge: 'green' },
] as const;

// ─── DOCUMENT PACKETS (surgical paperwork pre-fill) ──────────────────────────
// Author-time (platform_admin): fillable-PDF template upload → auto-detected
// AcroForm fields → token mapping → sample preview → immutable publish; packet
// (named template set) CRUD + per-practice visibility.
export const documentTemplateApi = {
  tokens: () => apiClient.get('/document-templates/tokens'),
  list: (practiceId: string) => apiClient.get('/document-templates', { params: { practice_id: practiceId } }),
  get: (id: string) => apiClient.get(`/document-templates/${id}`),
  // formData must include practice_id (the owning client) for a new template.
  upload: (formData: FormData) =>
    apiClient.post('/document-templates', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // In-browser field designer (Model A): flat PDF + a `fields` JSON of placed
  // boxes → backend injects token-named AcroForm fields → same draft pipeline.
  design: (formData: FormData) =>
    apiClient.post('/document-templates/design', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateMeta: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/document-templates/${id}`, data),
  setFieldMap: (id: string, versionId: string, fieldMap: Record<string, string>) =>
    apiClient.put(`/document-templates/${id}/versions/${versionId}/field-map`, { field_map: fieldMap }),
  preview: (id: string, versionId: string) =>
    apiClient.get(`/document-templates/${id}/versions/${versionId}/preview`, { responseType: 'blob' }),
  publish: (id: string, versionId: string) =>
    apiClient.post(`/document-templates/${id}/versions/${versionId}/publish`),
};

// Run-time (coordinator on a surgical task): list visible packets →
// preview-context (verify-before-finalize) → generate (REQUIRES attest:true —
// D2 human-in-the-loop invariant) → download ZIP of separate pre-filled PDFs.
export const documentPacketApi = {
  // admin (per practice — platform_admin authoring on the client's behalf)
  createPacket: (data: Record<string, unknown>) => apiClient.post('/document-packets', data),
  listAdmin: (practiceId: string) => apiClient.get('/document-packets/admin', { params: { practice_id: practiceId } }),
  updatePacket: (id: string, data: Record<string, unknown>) => apiClient.put(`/document-packets/${id}`, data),
  setItems: (id: string, items: unknown[]) => apiClient.put(`/document-packets/${id}/items`, { items }),
  // runtime
  listVisible: () => apiClient.get('/document-packets'),
  previewContext: (id: string, taskId: string) =>
    apiClient.post(`/document-packets/${id}/preview-context`, { task_id: taskId }),
  generate: (id: string, taskId: string, overrides?: Record<string, string>) =>
    apiClient.post(`/document-packets/${id}/generate`, { task_id: taskId, attest: true, overrides }),
  listBatches: (taskId: string) =>
    apiClient.get('/document-packets/generated', { params: { task_id: taskId } }),
  batchDownloadUrl: (batchId: string) =>
    apiClient.get(`/document-packets/generated/${batchId}/download`),
};

// ─── AI ASSIST — the prompt library (mig 252/253) ─────────────────────────────
// Admin-only config (ai.339). The library holds GENERIC prompts and prompts OWNED by one practice.
// The server gates it requirePromptLibraryAuthor(): the platform admin, or the account manager of the
// SELECTED practice (the X-Practice-ID the interceptor attaches), who only DRAFTS. Publish, archive,
// rename/move (meta) and scope are the platform admin's. The per-practice switch routes act on the
// selected practice only. A practice admin reaches none of it — founder ruling, build-scope point 12.
//
// `models` is served FROM CODE by the backend allow-list, never from a table, because on
// Bedrock the model id IS the data-residency control. The screen must offer only what this
// returns; an id the server would refuse should never be selectable.
export const aiAssistApi = {
  // the global library
  listModels: () => apiClient.get('/ai-assist/models'),
  listPrompts: () => apiClient.get('/ai-assist/prompts'),
  listVersions: (groupId: string) => apiClient.get(`/ai-assist/prompts/${groupId}/versions`),
  // N01 — `practiceId` is the practice the Save was CLICKED at, sent as this request's own X-Practice-ID
  // (the interceptor never overwrites a caller-set one), so a Save paused offline and replayed after a
  // switch at the top still goes to that practice. The server validates the header as it does any other.
  createPrompt: (data: {
    category: string; name: string; description?: string | null; display_order?: number;
    prompt_text: string; expected_output: string; model_id?: string | null;
    output_kind?: 'prose' | 'cover_suggestions';
    // ai.339 — REQUIRED, an explicit choice: 'practice' is owned by the SELECTED practice (never a body value).
    scope: 'generic' | 'practice';
  }, practiceId?: string | null) =>
    apiClient.post('/ai-assist/prompts', data, practiceId ? { headers: { 'X-Practice-ID': practiceId } } : undefined),
  // ai.339 — the category headings already in use (heading text only), for the create/rename datalist.
  listCategories: () => apiClient.get('/ai-assist/prompts/categories'),
  // Edits a DRAFT. A published version is immutable — the server 409s NOT_A_DRAFT and the
  // caller opens a new draft instead.
  updateDraft: (promptId: string, data: { prompt_text: string; expected_output: string; model_id?: string | null }) =>
    apiClient.patch(`/ai-assist/prompts/versions/${promptId}`, data),
  openNewDraft: (groupId: string) => apiClient.post(`/ai-assist/prompts/${groupId}/draft`),
  // Publishing retires the previous version in the same transaction; it changes the question
  // EVERY assigned practice is asked, which is why the screen confirms first.
  // F03 / N02 — `contentHash` is the content_hash of the draft wording the platform admin last READ in
  // the screen's wording view (the screen pins it there, and sends no token it did not pin). The server
  // publishes the draft only while its wording still has that hash, and answers 409 DRAFT_CHANGED
  // (writing nothing) if the draft was saved since. It cannot tell whether the wording was read.
  publish: (promptId: string, contentHash: string) =>
    apiClient.post(`/ai-assist/prompts/versions/${promptId}/publish`, { content_hash: contentHash }),
  setArchived: (groupId: string, archived: boolean) =>
    apiClient.patch(`/ai-assist/prompts/${groupId}/archive`, { archived }),
  // ai.339 — platform admin only: rename / re-category (owner and kind are not editable here).
  setMeta: (groupId: string, data: { category: string; name: string; description?: string | null; display_order?: number }) =>
    apiClient.patch(`/ai-assist/prompts/${groupId}/meta`, data),
  // ai.339 — platform admin only: generic, or owned by the SELECTED practice. 409 SCOPE_BLOCKED names the practices.
  // N01 — `practiceId` is the practice the change was CLICKED at, pinned on this request as for createPrompt.
  setScope: (groupId: string, scope: 'generic' | 'practice', practiceId?: string | null) =>
    apiClient.post(`/ai-assist/prompts/${groupId}/scope`, { scope }, practiceId ? { headers: { 'X-Practice-ID': practiceId } } : undefined),
  // per-practice switch — the caller passes the SELECTED practice; the server refuses any other.
  setAssignment: (practiceId: string, groupId: string, enabled: boolean) =>
    apiClient.put(`/ai-assist/practices/${practiceId}/prompts/${groupId}`, { enabled }),
  removeAssignment: (practiceId: string, groupId: string) =>
    apiClient.delete(`/ai-assist/practices/${practiceId}/prompts/${groupId}`),
};

// Per-practice access core (DESIGN 3.3 / 3.7) — the ONE client for /api/access. The practice is
// explicit in the URL on every call except `me`, which answers for the SELECTED practice (the
// X-Practice-ID the request interceptor attaches). Staff identity + permission facts only, no PHI.
// The server declares what may be done and why; callers render its codes, never re-derive a rule.
// The change set is the ONE write door: `dry_run: true` previews (diff, required reason and
// confirmations, every refusal), `dry_run: false` applies all-or-nothing for that practice.
export type AccessChangeSetOp = { user_id: string; key: string; op: 'grant' | 'revoke' };
export type AccessChangeSetSwitch = { module_key: string; value: boolean };
export type AccessChangeSetBody = {
  change_set_id?: string;
  dry_run: boolean;
  reason?: string | null;
  confirmations?: string[];
  ops?: AccessChangeSetOp[];
  switches?: AccessChangeSetSwitch[];
};
export type AccessHistoryFilter = { module?: string; user_id?: string; before?: string; limit?: number };
export type AccessOverviewPage = { after?: string | null; limit?: number };

export const accessApi = {
  practice: (practiceId: string, modules?: string[]) =>
    apiClient.get(`/access/practices/${encodeURIComponent(practiceId)}`, {
      params: modules && modules.length ? { modules: modules.join(',') } : {},
    }),
  changeSet: (practiceId: string, body: AccessChangeSetBody) =>
    apiClient.post(`/access/practices/${encodeURIComponent(practiceId)}/change-sets`, body),
  history: (practiceId: string, filter: AccessHistoryFilter = {}) =>
    apiClient.get(`/access/practices/${encodeURIComponent(practiceId)}/history`, { params: filter }),
  person: (userId: string) => apiClient.get(`/access/users/${encodeURIComponent(userId)}`),
  overview: (page: AccessOverviewPage = {}) => apiClient.get('/access/overview', { params: page }),
  me: () => apiClient.get('/access/me'),
};

// ─── OVERSIGHT — the Integrity report (ai.123 OPS-EXCEPTIONS) ─────────────────
// backend routes/oversight.ts. Every call is practice-scoped by the X-Practice-ID header the request
// interceptor adds; the server decides who may see (a 404 when not) and who may accept (a 403).
// `run` is a POST because a run may create and clear Integrity tasks (only when OVERSIGHT_MINT_TASKS
// is on); the counts it returns are read fresh on every call. Shapes: components/oversight/integrity.ts.
export const oversightApi = {
  // { enabled, mint_enabled, can_view, can_accept } for the selected practice
  access: () => apiClient.get('/oversight/access'),
  run: (moduleKey: string) => apiClient.post(`/oversight/${encodeURIComponent(moduleKey)}/run`),
  findings: (moduleKey: string, checkKey: string) =>
    apiClient.get(`/oversight/${encodeURIComponent(moduleKey)}/findings/${encodeURIComponent(checkKey)}`),
  accepted: (moduleKey: string, checkKey: string) => apiClient.get(`/oversight/${encodeURIComponent(moduleKey)}/accepted/${encodeURIComponent(checkKey)}`),
  accept: (taskId: string, payload: AcceptPayload) =>
    apiClient.post(`/oversight/${encodeURIComponent(taskId)}/accept`, payload),
  recheck: (taskId: string) => apiClient.post(`/oversight/${encodeURIComponent(taskId)}/recheck`),
};
